import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { themes, switchStyle } from "services/constants";
import { ShiftWorkTable } from "../Component/ShiftWorkTable";
import { Check, Delete, Edit } from "@mui/icons-material";
import {
  sftType,
  otPreauthorizationOptions,
  WeekoffPreauthorizationOptions,
  holidayPreauthorizationOptions,
  otTimingOptions,
  calculationOptions,
  holidayCalculationOptions,
  weekoffCalculationOptions,
  weekoffPaymentBasedOptions,
  holidayPaymentBasedOptions,
  otPaymentOptions,
  ShiftWorkExample,
  PaymentBasedAttendanceExample,
  PaymentBasedHolidayAttendanceExample,
  PaymentBasedWeekoffAttendanceExample,
  PaymentBasedFixedAmountExample,
  boxStyle,
  isAbsenceOptions,
  IsAbsenceExample,
  IsAbsencePaidExample,
  CustomBox,
  optionsStyle,
  individualBoxStyle,
  amtFactorStyle,
  infoMessageStyle,
  additionalInfoStyle,
  cardStyle,
  mainOptions,
} from "../Component/ShiftCategoryOptions";
import { CustomButton } from "components/CustomComponents";
import { CustomAutoComplete } from "components/CustomComponents";
import { useFetch } from "services/hooks/useFetch";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";

export const Earning = ({
  onUpdate,
  handleNext,
  handleBack,
  data,
  clearAbsenceValues,
  setClearAbsenceValues,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    resetField,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      is_overtime_allowed: false,
      ot_work_preauthorization: "Automatic",
      week_off_work_pre_authorization: "Automatic",
      holiday_work_preauthorization: "Automatic",
      ot_timing_privilege: "Only Before",
      calculation_based_on: "Hours",
      holiday_calculation_based_on: "Hours",
      weekoff_partial_shift_additional_percentage: 0,
      weekoff_partial_shift_count: 0,
      weekoff_fixed_amount_thres_hold: 0,
      weekoff_fixed_amount_thres_hold_consideration: 0,
      weekoff_fixed_amount_additional_thres_hold: 0,
      holiday_sft_payment_factor: 0,
      weekoff_fixed_amount_additional_thres_hold_consideration: 0,
      weekoff_calculation_based_on: "Hours",
      weekoff_thres_hold_limit: 0,
      weekoff_thres_hold_consideration: 0,
      weekoff_additional_thres_hold: 0,
      weekoff_additional_thres_hold_consideration: 0,
      week_off_payment_factor: 0,
      ot_sft_payment_factor: 0,
      ot_attendance_payment_factor: 1,
      minutes_for_consideration: 0,
      additional_minutes_for_consideration: 0,
      deduct_additional_percentage: 0,
      deduct_shift_count: 0,
      minimum_ot_consideration: 0,
      additional_ot: 0,
      ot_fixed_amount_thereshold_limit: 0,
      ot_fixed_amount_payable: 0,
      ot_fixed_amount_additional_mins: 0,
      ot_fixed_amount_additional_payable: 0,
      is_ot_pay_by_default: false,
      is_ot_payment_based_on: "Attendance",
      ot_equivalent_hours_configurations: [],
      is_weekoff_payment_based_on: "Attendance",
      is_paid_weekoff: false,
      is_payment_for_weekoff_day_attendance: false,
      payment_for_week_off_attendance: false,
      is_holiday_payment_based_on: "Attendance",
      is_paid_holiday: false,
      is_payment_for_holiday_day_attendance: false,
      holiday_attendance_payment_factor: 0,
      holiday_attendance_fixed_amount: 0,
      weekoff_attendance_payment_factor: 0,
      weekoff_attendance_fixed_amount: 0,
      attendance_deduct_type: "",
      fixed_amount_deduct_type: "",
      is_allow_payment_factor_for_ot: false,
      is_add_remaining_ot_to_base_factor: true,
      is_add_remaining_weekoff_to_base_factor: true,
      is_add_remaining_holiday_to_base_factor: true,
      is_allow_payment_factor_for_weekoff: false,
      is_allow_payment_factor_for_holiday: false,
      is_weekoff_paid: false,
      as_per_week_day_for_week_off: false,
      as_per_week_day_for_holiday: false,
      is_holiday_paid: false,
      is_absence_paid: false,
      absent_payment_based_on: "Paid",
      partial_payment_factor: 1,
      holiday_thres_hold_limit: 0,
      holiday_thres_hold_consideration: 0,
      holiday_additional_thres_hold: 0,
      holiday_additional_thres_hold_consideration: 0,
      holiday_fixed_amount_thres_hold: 0,
      holiday_fixed_amount_payable: 0,
      holiday_partial_shift_additional_percentage: 0,
      holiday_partial_shift_count: 0,
      holiday_added_payroll_default: 0,
      is_weekoff_pay_by_default: 0,
      ot_payment_factor_configurations: [],
      weekoff_payment_factor_configurations: [],
      holiday_payment_factor_configurations: [],
      equivalent_hours_additional_minutes: 0,
      equivalent_hours_ot_minutes: 0,
      is_weekoff_time_bound_break_deduction_allowed: false,
      weekoff_time_bound_break_deduction_configurations: [],
      is_holiday_time_bound_break_deduction_allowed: false,
      holiday_time_bound_break_deduction_configurations: [],
    },
  });
  const { result: overtimeBands } = useFetch(
    domain
      ? `${domain}administration/shiftroster/shiftrota/overtime-bands/`
      : "",
  );
  const { result: complianceData } = useFetch(
    domain ? `${domain}compliance/` : "",
  );
  const isBandAllowed = complianceData?.is_band_allowed === true;
  const bandOptions = useMemo(
    () =>
      Array.isArray(overtimeBands)
        ? overtimeBands
        : Array.isArray(overtimeBands?.responseDetails)
          ? overtimeBands.responseDetails
          : [],
    [overtimeBands],
  );
  const [otPaymentFactorRows, setOtPaymentFactorRows] = useState([]);
  const [otPaymentFactorErrors, setOtPaymentFactorErrors] = useState([]);
  const [weekoffPaymentFactorRows, setWeekoffPaymentFactorRows] = useState([]);
  const [weekoffPaymentFactorErrors, setWeekoffPaymentFactorErrors] = useState(
    [],
  );
  const [holidayPaymentFactorRows, setHolidayPaymentFactorRows] = useState([]);
  const [holidayPaymentFactorErrors, setHolidayPaymentFactorErrors] = useState(
    [],
  );
  const [otEquivalentHoursRows, setOtEquivalentHoursRows] = useState([
    { min_hours: "", credited_hours: "" },
  ]);
  const [otEquivalentHoursErrors, setOtEquivalentHoursErrors] = useState([
    { min_hours: false, credited_hours: false },
  ]);
  const [weekoffTimeBoundBreakRules, setWeekoffTimeBoundBreakRules] = useState(
    [],
  );
  const [weekoffTimeBoundBreakErrors, setWeekoffTimeBoundBreakErrors] =
    useState([]);
  const [holidayTimeBoundBreakRules, setHolidayTimeBoundBreakRules] = useState(
    [],
  );
  const [holidayTimeBoundBreakErrors, setHolidayTimeBoundBreakErrors] =
    useState([]);
  const [otPaymentFactorDraftRows, setOtPaymentFactorDraftRows] = useState([]);
  const [otPaymentFactorDraftErrors, setOtPaymentFactorDraftErrors] = useState(
    [],
  );
  const [openPaymentFactorDialog, setOpenPaymentFactorDialog] = useState(false);
  const [paymentFactorDialogType, setPaymentFactorDialogType] = useState("ot");
  const [rows, setRows] = useState([
    { work_min_percentage: "", count_as_shift: "" },
  ]);
  const [rowError, setRowError] = useState([
    { work_min_percentage: false, count_as_shift: false },
  ]);

  const [weekoffRows, setWeekoffRows] = useState([
    { work_min_percentage: "", count_as_shift: "" },
  ]);
  const [weekoffRowError, setWeekoffRowError] = useState([
    { work_min_percentage: false, count_as_shift: false },
  ]);

  const [holidayRows, setHolidayRows] = useState([
    { work_min_percentage: "", count_as_shift: "" },
  ]);
  const [holidayRowError, setHolidayRowError] = useState([
    { work_min_percentage: false, count_as_shift: false },
  ]);

  const validateTableRef = useRef(null);
  const validateTableRefWeekoff = useRef(null);
  const validateTableRefHoliday = useRef(null);
  const hasInitializedData = useRef(false);
  const hasMounted = useRef(false);
  const skipNextPaymentFactorPersist = useRef(false);
  const skipNextEquivalentHoursValidation = useRef(false);
  const skipNextWeekoffTimeBoundBreakPersist = useRef(false);
  const skipNextHolidayTimeBoundBreakPersist = useRef(false);
  const lastPersistedPaymentFactors = useRef({
    ot: "[]",
    weekoff: "[]",
    holiday: "[]",
    equivalent: "[]",
  });
  const lastPersistedWeekoffTimeBoundBreak = useRef("[]");
  const lastPersistedHolidayTimeBoundBreak = useRef("[]");

  const formatBandFactor = useCallback((value) => {
    if (value === null || value === undefined || value === "") return "";
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? String(value) : String(numericValue);
  }, []);

  const timeToMinutes = useCallback((timeValue) => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/.test(timeValue || "")) {
      return null;
    }
    const [hours, minutes] = timeValue.split(":").map(Number);
    return hours * 60 + minutes;
  }, []);

  const getPaymentFactorEndMinutes = useCallback(
    (timeValue) => {
      if (timeValue === "23:59") {
        return 1440;
      }

      return timeToMinutes(timeValue);
    },
    [timeToMinutes],
  );

  const normalizePaymentFactorTime = useCallback((timeValue) => {
    if (!timeValue || typeof timeValue !== "string") {
      return "";
    }

    if (/^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
      return timeValue.slice(0, 5);
    }

    return timeValue;
  }, []);

  const createEmptyPaymentFactorRow = useCallback(
    () => ({
      overtime_band: "",
      band_name: "",
      factor: "",
      start_time: "",
      end_time: "",
      isEditing: true,
    }),
    [],
  );

  const createEmptyEquivalentHoursRow = useCallback(
    () => ({
      min_hours: "",
      credited_hours: "",
    }),
    [],
  );

  const markEarningInteraction = useCallback(() => {
    hasInitializedData.current = true;
  }, []);

  const isEmptyPaymentFactorRow = useCallback(
    (row) => !row?.overtime_band && !row?.start_time && !row?.end_time,
    [],
  );

  const hasInitialEarningData = (payload) => {
    if (!payload || Object.keys(payload).length === 0) return false;

    return [
      "rows",
      "weekoffRows",
      "holidayRows",
      "is_overtime_allowed",
      "calculation_based_on",
      "is_ot_payment_based_on",
      "ot_payment_factor_configurations",
      "ot_equivalent_hours_configurations",
      "is_weekoff_payment_based_on",
      "weekoff_payment_factor_configurations",
      "is_holiday_payment_based_on",
      "holiday_payment_factor_configurations",
      "is_absence_paid",
      "absent_payment_based_on",
      "payment_for_week_off_attendance",
      "is_payment_for_holiday_day_attendance",
      "attendance_deduct_type",
      "fixed_amount_deduct_type",
      "is_weekoff_time_bound_break_deduction_allowed",
      "weekoff_time_bound_break_deduction_configurations",
      "is_holiday_time_bound_break_deduction_allowed",
      "holiday_time_bound_break_deduction_configurations",
    ].some((field) => payload[field] !== undefined);
  };

  const getConfiguredPaymentFactorRows = useCallback(
    (paymentFactorRows) =>
      Array.isArray(paymentFactorRows)
        ? paymentFactorRows.filter((row) => !isEmptyPaymentFactorRow(row))
        : [],
    [isEmptyPaymentFactorRow],
  );

  const getConfiguredEquivalentHoursRows = useCallback(
    (equivalentRows) =>
      Array.isArray(equivalentRows)
        ? equivalentRows.filter(
            (row) => row?.min_hours !== "" || row?.credited_hours !== "",
          )
        : [],
    [],
  );

  const normalizeEquivalentHoursTime = useCallback((timeValue) => {
    if (!timeValue || typeof timeValue !== "string") {
      return "";
    }

    if (/^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
      return timeValue.slice(0, 5);
    }

    return timeValue;
  }, []);

  const timeBoundBreakDeductFromOptions = [
    { value: "start_time", label: "Start" },
    { value: "mid_time", label: "Mid" },
    { value: "end_time", label: "End" },
  ];

  const createEmptyTimeBoundBreakRule = useCallback(
    () => ({
      hours: 0,
      minutes: 0,
      deduct_minutes: 0,
      deduct_from: "start_time",
    }),
    [],
  );

  // Backend stores the threshold as separate hours/minutes, so the picker's
  // dayjs value is built from and split back into those two fields.
  const getTimeBoundBreakPickerValue = useCallback((rule) => {
    if (rule?.hours === "" || rule?.minutes === "") {
      return null;
    }
    const hours = Number(rule?.hours);
    const minutes = Number(rule?.minutes);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }
    return dayjs().hour(Math.min(hours, 23)).minute(minutes).second(0);
  }, []);

  const mapTimeBoundBreakConfigRows = useCallback(
    (configRows) =>
      Array.isArray(configRows)
        ? configRows.map((item) => ({
            hours: item?.hours ?? 0,
            minutes: item?.minutes ?? 0,
            deduct_minutes: item?.deduct_minutes ?? 0,
            deduct_from: item?.deduct_from || "start_time",
          }))
        : [],
    [],
  );

  const getConfiguredTimeBoundBreakRows = useCallback(
    (rulesRows) =>
      Array.isArray(rulesRows)
        ? rulesRows.filter(
            (row) =>
              row?.hours !== "" ||
              row?.minutes !== "" ||
              row?.deduct_minutes !== "",
          )
        : [],
    [],
  );

  const validateTimeBoundBreakRuleRows = useCallback(
    (rulesRows) =>
      rulesRows.map((row) => {
        if (
          row?.hours === "" &&
          row?.minutes === "" &&
          row?.deduct_minutes === ""
        ) {
          return { hours: false, minutes: false, deduct_minutes: false };
        }

        const hours = Number(row?.hours);
        const minutes = Number(row?.minutes);
        const deductMinutes = Number(row?.deduct_minutes);

        return {
          hours:
            row?.hours === "" || Number.isNaN(hours) || hours < 0 || hours > 24,
          minutes:
            row?.minutes === "" ||
            Number.isNaN(minutes) ||
            minutes < 0 ||
            minutes > 59,
          deduct_minutes:
            row?.deduct_minutes === "" ||
            Number.isNaN(deductMinutes) ||
            deductMinutes < 1 ||
            deductMinutes > 120,
        };
      }),
    [],
  );

  const hasTimeBoundBreakErrors = useCallback(
    (rowErrors) =>
      rowErrors.some(
        (row) => row?.hours || row?.minutes || row?.deduct_minutes,
      ),
    [],
  );

  const paymentFactorFieldNames = {
    ot: "ot_payment_factor_configurations",
    weekoff: "weekoff_payment_factor_configurations",
    holiday: "holiday_payment_factor_configurations",
  };

  const mapPaymentFactorRows = useCallback(
    (paymentFactorRows) =>
      Array.isArray(paymentFactorRows)
        ? paymentFactorRows.map((row) => ({
            overtime_band:
              row.overtime_band?.id ??
              row.overtime_band ??
              row.band_id ??
              row.id ??
              "",
            band_name:
              row.work_band_name ??
              row.band_name ??
              row.overtime_band?.band_name ??
              row.overtime_band_name ??
              "",
            factor: formatBandFactor(
              row.factor ?? row.payment_factor ?? row.band_factor,
            ),
            start_time: normalizePaymentFactorTime(row.start_time),
            end_time: normalizePaymentFactorTime(row.end_time),
            isEditing: false,
          }))
        : [],
    [formatBandFactor, normalizePaymentFactorTime],
  );

  const clearPaymentFactorRows = (type) => {
    syncPaymentFactorRows(type, []);
  };

  const syncPaymentFactorRows = (type, rows) => {
    markEarningInteraction();
    const configuredRows = getConfiguredPaymentFactorRows(rows);
    const rowsToStore = configuredRows.length
      ? configuredRows
      : [createEmptyPaymentFactorRow()];
    const fieldName = paymentFactorFieldNames[type];

    if (type === "ot") {
      setOtPaymentFactorRows(rowsToStore);
      setOtPaymentFactorErrors([]);
    }
    if (type === "weekoff") {
      setWeekoffPaymentFactorRows(rowsToStore);
      setWeekoffPaymentFactorErrors([]);
    }
    if (type === "holiday") {
      setHolidayPaymentFactorRows(rowsToStore);
      setHolidayPaymentFactorErrors([]);
    }

    setValue(fieldName, configuredRows, {
      shouldValidate: false,
      shouldDirty: true,
    });
    onUpdate({ [fieldName]: configuredRows });
  };

  const paymentFactorConfig = {
    ot: {
      title: "Payment Factor Details",
      description:
        "Choose how overtime payment is calculated based on work bands and applicable time duration.",
      dialogDescription: "Add, edit or remove overtime payment factor rows.",
      emptyMessage: "No overtime bands configured.",
      rows: otPaymentFactorRows,
      errors: otPaymentFactorErrors,
      setRows: setOtPaymentFactorRows,
      setErrors: setOtPaymentFactorErrors,
    },
    weekoff: {
      title: "Payment Factor Details",
      description:
        "Choose how weekoff payment is calculated based on work bands and time duration.",
      dialogDescription: "Add, edit or remove weekoff payment factor rows.",
      emptyMessage: "No overtime bands configured.",
      rows: weekoffPaymentFactorRows,
      errors: weekoffPaymentFactorErrors,
      setRows: setWeekoffPaymentFactorRows,
      setErrors: setWeekoffPaymentFactorErrors,
    },
    holiday: {
      title: "Payment Factor Details",
      description:
        "Choose how holiday payment is calculated based on work bands and time duration.",
      dialogDescription: "Add, edit or remove holiday payment factor rows.",
      emptyMessage: "No overtime bands configured.",
      rows: holidayPaymentFactorRows,
      errors: holidayPaymentFactorErrors,
      setRows: setHolidayPaymentFactorRows,
      setErrors: setHolidayPaymentFactorErrors,
    },
  };

  const handleOpenPaymentFactorDialog = (type = "ot") => {
    markEarningInteraction();
    const config = paymentFactorConfig[type] ?? paymentFactorConfig.ot;
    const selectedRows = getConfiguredPaymentFactorRows(config.rows);
    const draftRows = selectedRows.length
      ? selectedRows.map((row) => {
          const matchingBand = bandOptions.find(
            (band) =>
              band.id === row.overtime_band ||
              band.band_name === row.band_name ||
              band.band_name === row.work_band_name,
          );

          return {
            ...row,
            overtime_band: matchingBand?.id ?? row.overtime_band ?? "",
            band_name:
              matchingBand?.band_name ??
              row.band_name ??
              row.work_band_name ??
              "",
            factor: formatBandFactor(
              matchingBand?.band_factor ?? row.factor ?? row.payment_factor,
            ),
            isEditing: false,
          };
        })
      : [createEmptyPaymentFactorRow()];
    setPaymentFactorDialogType(paymentFactorConfig[type] ? type : "ot");
    setOtPaymentFactorDraftRows(draftRows);
    setOtPaymentFactorDraftErrors(getBasePaymentFactorErrors(draftRows));
    setOpenPaymentFactorDialog(true);
  };

  const handleAddPaymentFactorRow = () => {
    markEarningInteraction();
    const nextErrors = getRequiredPaymentFactorErrors(otPaymentFactorDraftRows);
    setOtPaymentFactorDraftErrors(nextErrors);

    if (hasOtPaymentFactorErrors(nextErrors)) {
      return;
    }

    setOtPaymentFactorDraftRows((prev) => [
      ...prev,
      createEmptyPaymentFactorRow(),
    ]);
  };

  const handleClosePaymentFactorDialog = () => {
    setOtPaymentFactorDraftRows([]);
    setOtPaymentFactorDraftErrors([]);
    setOpenPaymentFactorDialog(false);
  };

  const handlePaymentFactorDraftBandChange = (index, bandId) => {
    markEarningInteraction();
    const selectedBand = bandOptions.find((band) => band.id === bandId);
    setOtPaymentFactorDraftRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              overtime_band: selectedBand?.id ?? "",
              band_name: selectedBand?.band_name ?? "",
              factor: formatBandFactor(selectedBand?.band_factor),
            }
          : row,
      ),
    );
  };

  const handleEditPaymentFactorDraftRow = (index) => {
    markEarningInteraction();
    setOtPaymentFactorDraftRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, isEditing: true } : row,
      ),
    );
  };

  const handleSavePaymentFactorDraftRow = (index) => {
    markEarningInteraction();
    const nextErrors = getRequiredPaymentFactorErrors(otPaymentFactorDraftRows);
    setOtPaymentFactorDraftErrors(nextErrors);
    const currentRowError = nextErrors[index];

    if (
      currentRowError?.overtime_band ||
      currentRowError?.start_time ||
      currentRowError?.end_time ||
      currentRowError?.range
    ) {
      return;
    }

    setOtPaymentFactorDraftRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, isEditing: false } : row,
      ),
    );
  };

  const handleDeletePaymentFactorDraftRow = (index) => {
    markEarningInteraction();
    setOtPaymentFactorDraftRows((prev) => {
      const nextRows = prev.filter((_, rowIndex) => rowIndex !== index);
      return nextRows.length > 0 ? nextRows : [createEmptyPaymentFactorRow()];
    });
    setOtPaymentFactorDraftErrors((prev) =>
      prev.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  const getPaymentFactorBandOptionsForRow = useCallback(
    (rows, index) => {
      const currentBandId = rows[index]?.overtime_band;
      const selectedBandIds = rows
        .map((row, rowIndex) => (rowIndex === index ? null : row.overtime_band))
        .filter(Boolean);

      return bandOptions.filter(
        (band) =>
          band.id === currentBandId || !selectedBandIds.includes(band.id),
      );
    },
    [bandOptions],
  );

  const getTimePickerValue = (timeValue) => {
    if (!timeValue) return null;
    const parsed = dayjs(timeValue, "HH:mm");
    return parsed.isValid() ? parsed : null;
  };

  const handlePaymentFactorDraftTimeChange = (index, key, value) => {
    let normalizedValue = "";
    if (value && value.format) {
      normalizedValue = value.format("HH:mm");
    } else if (typeof value === "string") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      normalizedValue =
        digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
    }

    setOtPaymentFactorDraftRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: normalizedValue } : row,
      ),
    );
  };

  const getPaymentFactorTimeHelperText = (row, rowError, key) => {
    if (!rowError[key] && !rowError.range) return "";
    if (rowError[key]) {
      if (key === "end_time" && row.end_time === "00:00") {
        return "Use 23:59 for day end";
      }
      return "Use HH:mm";
    }
    return "Complete 24-hour cycle with no gaps or overlaps";
  };

  const getBasePaymentFactorErrors = useCallback(
    (paymentFactorRows) => {
      const ranges = [];
      const nextErrors = paymentFactorRows.map((row) => {
        if (isEmptyPaymentFactorRow(row)) {
          return {
            overtime_band: false,
            start_time: false,
            end_time: false,
            range: false,
          };
        }

        const start = timeToMinutes(row.start_time);
        const end = getPaymentFactorEndMinutes(row.end_time);
        const rowError = {
          overtime_band: !row.overtime_band,
          start_time: start === null || start >= 1440,
          end_time: end === null || end === 0 || end > 1440,
          range: false,
        };

        if (
          !rowError.overtime_band &&
          !rowError.start_time &&
          !rowError.end_time &&
          !rowError.range
        ) {
          if (start < end) {
            ranges.push({ start, end });
          } else {
            ranges.push({ start, end: 1440 });
            ranges.push({ start: 0, end });
          }
        }

        return rowError;
      });

      const sortedRanges = [...ranges].sort((a, b) => a.start - b.start);
      const hasOverlap = sortedRanges.some((range, index) =>
        index === 0 ? false : range.start < sortedRanges[index - 1].end,
      );

      return hasOverlap
        ? nextErrors.map((row) => ({ ...row, range: true }))
        : nextErrors;
    },
    [getPaymentFactorEndMinutes, isEmptyPaymentFactorRow, timeToMinutes],
  );

  const getRequiredPaymentFactorErrors = useCallback(
    (paymentFactorRows) =>
      getBasePaymentFactorErrors(paymentFactorRows).map((rowError, index) => {
        const row = paymentFactorRows[index] ?? {};

        return {
          ...rowError,
          overtime_band: rowError.overtime_band || !row.overtime_band,
          start_time: rowError.start_time || !row.start_time,
          end_time: rowError.end_time || !row.end_time,
        };
      }),
    [getBasePaymentFactorErrors],
  );

  const getOtPaymentFactorErrors = useCallback(
    (paymentFactorRows) => {
      const nextErrors = getRequiredPaymentFactorErrors(paymentFactorRows);

      if (
        nextErrors.some(
          (row) =>
            row.overtime_band || row.start_time || row.end_time || row.range,
        )
      ) {
        return nextErrors;
      }

      const configuredRows = paymentFactorRows.filter(
        (row) => !isEmptyPaymentFactorRow(row),
      );
      const ranges = configuredRows
        .flatMap((row) => {
          const start = timeToMinutes(row.start_time);
          const end = getPaymentFactorEndMinutes(row.end_time);

          if (start === null || end === null) {
            return [];
          }

          return start < end
            ? [{ start, end }]
            : [
                { start, end: 1440 },
                { start: 0, end },
              ];
        })
        .sort((a, b) => a.start - b.start);

      const isFullCycle =
        ranges.length > 0 &&
        ranges[0].start === 0 &&
        ranges[ranges.length - 1].end === 1440 &&
        ranges.every((range, index) =>
          index === 0 ? true : range.start === ranges[index - 1].end,
        );

      return isFullCycle
        ? nextErrors
        : nextErrors.map((row, index) =>
            isEmptyPaymentFactorRow(paymentFactorRows[index])
              ? row
              : { ...row, range: true },
          );
    },
    [
      getPaymentFactorEndMinutes,
      getRequiredPaymentFactorErrors,
      isEmptyPaymentFactorRow,
      timeToMinutes,
    ],
  );

  const hasOtPaymentFactorErrors = (paymentFactorErrors) =>
    paymentFactorErrors.some(
      (row) => row.overtime_band || row.start_time || row.end_time || row.range,
    );

  const validateOtPaymentFactorRows = () => {
    if (
      !watch("is_overtime_allowed") ||
      watch("calculation_based_on") !== "Hours" ||
      watch("is_ot_payment_based_on") !== "Attendance" ||
      bandOptions.length === 0
    ) {
      setOtPaymentFactorErrors([]);
      return true;
    }

    const configuredRows = getConfiguredPaymentFactorRows(otPaymentFactorRows);
    if (configuredRows.length === 0) {
      setOtPaymentFactorErrors([]);
      return true;
    }

    const rowsToValidate = otPaymentFactorRows;
    const nextErrors = getOtPaymentFactorErrors(rowsToValidate);
    setOtPaymentFactorErrors(nextErrors);
    return !hasOtPaymentFactorErrors(nextErrors);
  };

  const validateWeekoffPaymentFactorRows = () => {
    if (
      !watch("payment_for_week_off_attendance") ||
      watch("weekoff_calculation_based_on") !== "Hours" ||
      watch("is_weekoff_payment_based_on") !== "Attendance" ||
      bandOptions.length === 0
    ) {
      setWeekoffPaymentFactorErrors([]);
      return true;
    }

    const configuredRows = getConfiguredPaymentFactorRows(
      weekoffPaymentFactorRows,
    );
    if (configuredRows.length === 0) {
      setWeekoffPaymentFactorErrors([]);
      return true;
    }

    const rowsToValidate = weekoffPaymentFactorRows;
    const nextErrors = getOtPaymentFactorErrors(rowsToValidate);
    setWeekoffPaymentFactorErrors(nextErrors);
    return !hasOtPaymentFactorErrors(nextErrors);
  };

  const validateHolidayPaymentFactorRows = () => {
    if (
      !watch("is_payment_for_holiday_day_attendance") ||
      watch("holiday_calculation_based_on") !== "Hours" ||
      watch("is_holiday_payment_based_on") !== "Attendance" ||
      bandOptions.length === 0
    ) {
      setHolidayPaymentFactorErrors([]);
      return true;
    }

    const configuredRows = getConfiguredPaymentFactorRows(
      holidayPaymentFactorRows,
    );
    if (configuredRows.length === 0) {
      setHolidayPaymentFactorErrors([]);
      return true;
    }

    const rowsToValidate = holidayPaymentFactorRows;
    const nextErrors = getOtPaymentFactorErrors(rowsToValidate);
    setHolidayPaymentFactorErrors(nextErrors);
    return !hasOtPaymentFactorErrors(nextErrors);
  };

  const validateOtEquivalentHoursTable = () => {
    if (
      !watch("is_overtime_allowed") ||
      watch("calculation_based_on") !== "Hours" ||
      watch("is_ot_payment_based_on") !== "Equivalent Hours"
    ) {
      setOtEquivalentHoursErrors([]);
      return true;
    }

    const nextErrors = validateEquivalentHoursRows(otEquivalentHoursRows);
    setOtEquivalentHoursErrors(nextErrors);
    return !hasEquivalentHoursErrors(nextErrors);
  };

  const handleOTPreauthorization = (event) => {
    const value = event.target.value;
    setValue("ot_work_preauthorization", value);
    setValue("ot_timing_privilege", "Only Before");
  };

  const handleWeekOffPreauthorization = (event) => {
    const value = event.target.value;
    setValue("week_off_work_pre_authorization", value);
  };

  const handleHolidayPreauthorization = (event) => {
    const value = event.target.value;
    setValue("holiday_work_preauthorization", value);
  };

  const hanldeOTPrivilege = (event) => {
    setValue("ot_timing_privilege", event.target.value);
  };

  const hanldeOTCalculationBasedOn = (event) => {
    const value = event.target.value;
    setValue("calculation_based_on", value);
    setValue("is_ot_payment_based_on", "Attendance");

    if (value === "Shift") {
      resetOtFixedAmount();
      resetOtAttendance();
      resetOtEquivalentHours();
    } else {
      resetOtSftTable();
    }
  };

  const hanldeWeekoffCalculationBasedOn = (event) => {
    const value = event.target.value;
    setValue("weekoff_calculation_based_on", value);
    setValue("is_weekoff_payment_based_on", "Attendance");

    if (value === "Shift") {
      resetWeekoffFixedAmount();
      resetWeekoffAttendance();
    } else {
      resetWeekoffSftTable();
    }
  };

  const hanldeHolidayCalculationBasedOn = (event) => {
    const value = event.target.value;
    setValue("holiday_calculation_based_on", value);
    setValue("is_holiday_payment_based_on", "Attendance");

    if (value === "Shift") {
      resetHolidayFixedAmount();
      resetHolidayAttendance();
    } else {
      resetHolidaySftTable();
    }
  };

  // const handleOTPay = (event) => {
  //   setValue("is_ot_pay_by_default", event.target.checked);
  // };

  const validateEquivalentHoursRows = useCallback(
    (equivalentRows) => {
      if (!Array.isArray(equivalentRows) || equivalentRows.length === 0) {
        return [{ min_hours: true, credited_hours: true, range: false }];
      }

      const minHoursMap = new Map();
      const nextErrors = equivalentRows.map((row, index) => {
        const minHours = normalizeEquivalentHoursTime(row?.min_hours);
        const creditedHours = normalizeEquivalentHoursTime(row?.credited_hours);
        const start = timeToMinutes(minHours);
        const credited = timeToMinutes(creditedHours);

        const rowError = {
          min_hours: start === null,
          credited_hours: credited === null,
          range: false,
        };

        if (!rowError.min_hours) {
          const duplicateIndexes = minHoursMap.get(minHours) ?? [];
          duplicateIndexes.push(index);
          minHoursMap.set(minHours, duplicateIndexes);
        }

        return rowError;
      });

      if (getConfiguredEquivalentHoursRows(equivalentRows).length === 0) {
        return nextErrors.map(() => ({
          min_hours: true,
          credited_hours: true,
          range: false,
        }));
      }

      const duplicateMinHoursIndexes = new Set();
      minHoursMap.forEach((indexes) => {
        if (indexes.length > 1) {
          indexes.forEach((itemIndex) =>
            duplicateMinHoursIndexes.add(itemIndex),
          );
        }
      });

      return nextErrors.map((rowError, index) => ({
        min_hours:
          rowError.min_hours ||
          normalizeEquivalentHoursTime(equivalentRows[index]?.min_hours) === "",
        credited_hours:
          rowError.credited_hours ||
          normalizeEquivalentHoursTime(
            equivalentRows[index]?.credited_hours,
          ) === "",
        range: duplicateMinHoursIndexes.has(index),
      }));
    },
    [
      getConfiguredEquivalentHoursRows,
      normalizeEquivalentHoursTime,
      timeToMinutes,
    ],
  );

  const hasEquivalentHoursErrors = useCallback(
    (rowErrors) =>
      rowErrors.some(
        (row) => row?.min_hours || row?.credited_hours || row?.range,
      ),
    [],
  );

  const handleOtEquivalentHoursChange = (index, field, value) => {
    markEarningInteraction();
    let normalizedValue = "";
    if (value && value.format) {
      normalizedValue = value.format("HH:mm");
    } else if (typeof value === "string") {
      normalizedValue = normalizeEquivalentHoursTime(value);
    }

    setOtEquivalentHoursRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: normalizedValue } : row,
      ),
    );
  };

  const handleAddOtEquivalentHoursRow = () => {
    markEarningInteraction();
    const nextErrors = validateEquivalentHoursRows(otEquivalentHoursRows);
    setOtEquivalentHoursErrors(nextErrors);

    if (hasEquivalentHoursErrors(nextErrors)) {
      return;
    }

    skipNextEquivalentHoursValidation.current = true;
    setOtEquivalentHoursRows((prev) => [
      ...prev,
      createEmptyEquivalentHoursRow(),
    ]);
    setOtEquivalentHoursErrors((prev) => [
      ...prev,
      { min_hours: false, credited_hours: false },
    ]);
  };

  const handleDeleteOtEquivalentHoursRow = (index) => {
    markEarningInteraction();
    const nextRows = otEquivalentHoursRows.filter(
      (_, rowIndex) => rowIndex !== index,
    );
    const rowsToStore =
      nextRows.length > 0 ? nextRows : [createEmptyEquivalentHoursRow()];
    const nextErrors = validateEquivalentHoursRows(rowsToStore);

    setOtEquivalentHoursRows(rowsToStore);
    setOtEquivalentHoursErrors(nextErrors);
    setValue(
      "ot_equivalent_hours_configurations",
      getConfiguredEquivalentHoursRows(rowsToStore),
      {
        shouldValidate: false,
        shouldDirty: true,
      },
    );
    onUpdate({
      ot_equivalent_hours_configurations:
        getConfiguredEquivalentHoursRows(rowsToStore),
    });
  };

  // const handleWeekoffPay = (event) => {
  //   setValue("is_weekoff_pay_by_default", event.target.checked);
  // };

  // const handleHolidayPay = (event) => {
  //   setValue("holiday_added_payroll_default", event.target.checked);
  // };

  const handleOTPaymentBasedOn = (event) => {
    markEarningInteraction();
    const value = event.target.value;
    setValue("is_ot_payment_based_on", value);
    if (value === "Attendance") {
      resetOtFixedAmount();
      resetOtEquivalentHours();
    } else if (value === "Equivalent Hours") {
      resetOtAttendance();
      resetOtFixedAmount();
    } else {
      resetOtAttendance();
      resetOtEquivalentHours();
    }
  };

  const handleHolidayPaymentBasedOn = (event) => {
    markEarningInteraction();
    const value = event.target.value;
    setValue("is_holiday_payment_based_on", value);
    if (value === "Attendance") {
      resetHolidayFixedAmount();
    } else {
      resetHolidayAttendance();
    }
  };

  const handleWeekoffPaymentBasedOn = (event) => {
    markEarningInteraction();
    const value = event.target.value;
    setValue("is_weekoff_payment_based_on", value);
    if (value === "Attendance") {
      resetWeekoffFixedAmount();
    } else {
      resetWeekoffAttendance();
    }
  };

  const handleAbsencePaymentBasedOn = (event) => {
    setValue("absent_payment_based_on", event.target.value);
    setValue("partial_payment_factor", 1);
  };

  const resetWeekoffTimeBoundBreakRules = () => {
    setWeekoffTimeBoundBreakRules([]);
    setWeekoffTimeBoundBreakErrors([]);
    setValue("weekoff_time_bound_break_deduction_configurations", [], {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  const handleWeekoffTimeBoundBreakToggle = (event) => {
    markEarningInteraction();
    const checked = event.target.checked;
    setValue("is_weekoff_time_bound_break_deduction_allowed", checked);
    if (checked) {
      if (weekoffTimeBoundBreakRules.length === 0) {
        setWeekoffTimeBoundBreakRules([createEmptyTimeBoundBreakRule()]);
        setWeekoffTimeBoundBreakErrors([
          { hours: false, minutes: false, deduct_minutes: false },
        ]);
      }
    } else {
      resetWeekoffTimeBoundBreakRules();
    }
  };

  const handleAddWeekoffTimeBoundBreakRule = () => {
    markEarningInteraction();
    setWeekoffTimeBoundBreakRules((prev) => [
      ...prev,
      createEmptyTimeBoundBreakRule(),
    ]);
    setWeekoffTimeBoundBreakErrors((prev) => [
      ...prev,
      { hours: false, minutes: false, deduct_minutes: false },
    ]);
  };

  const handleRemoveWeekoffTimeBoundBreakRule = (index) => {
    if (weekoffTimeBoundBreakRules.length <= 1) {
      return;
    }
    markEarningInteraction();
    setWeekoffTimeBoundBreakRules((prev) => prev.filter((_, i) => i !== index));
    setWeekoffTimeBoundBreakErrors((prev) =>
      prev.filter((_, i) => i !== index),
    );
  };

  const handleWeekoffTimeBoundBreakRuleChange = (index, field, value) => {
    markEarningInteraction();
    setWeekoffTimeBoundBreakRules((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    setWeekoffTimeBoundBreakErrors((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: false } : row)),
    );
  };

  const handleWeekoffTimeBoundBreakTimeChange = (index, newValue) => {
    if (!newValue || !newValue.isValid()) {
      handleWeekoffTimeBoundBreakRuleChange(index, "hours", "");
      handleWeekoffTimeBoundBreakRuleChange(index, "minutes", "");
      return;
    }
    handleWeekoffTimeBoundBreakRuleChange(index, "hours", newValue.hour());
    handleWeekoffTimeBoundBreakRuleChange(index, "minutes", newValue.minute());
  };

  const validateWeekoffTimeBoundBreakRules = () => {
    if (!watch("is_weekoff_time_bound_break_deduction_allowed")) {
      setWeekoffTimeBoundBreakErrors([]);
      return true;
    }

    const nextErrors = validateTimeBoundBreakRuleRows(
      weekoffTimeBoundBreakRules,
    );
    setWeekoffTimeBoundBreakErrors(nextErrors);
    return !hasTimeBoundBreakErrors(nextErrors);
  };

  const resetHolidayTimeBoundBreakRules = () => {
    setHolidayTimeBoundBreakRules([]);
    setHolidayTimeBoundBreakErrors([]);
    setValue("holiday_time_bound_break_deduction_configurations", [], {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  const handleHolidayTimeBoundBreakToggle = (event) => {
    markEarningInteraction();
    const checked = event.target.checked;
    setValue("is_holiday_time_bound_break_deduction_allowed", checked);
    if (checked) {
      if (holidayTimeBoundBreakRules.length === 0) {
        setHolidayTimeBoundBreakRules([createEmptyTimeBoundBreakRule()]);
        setHolidayTimeBoundBreakErrors([
          { hours: false, minutes: false, deduct_minutes: false },
        ]);
      }
    } else {
      resetHolidayTimeBoundBreakRules();
    }
  };

  const handleAddHolidayTimeBoundBreakRule = () => {
    markEarningInteraction();
    setHolidayTimeBoundBreakRules((prev) => [
      ...prev,
      createEmptyTimeBoundBreakRule(),
    ]);
    setHolidayTimeBoundBreakErrors((prev) => [
      ...prev,
      { hours: false, minutes: false, deduct_minutes: false },
    ]);
  };

  const handleRemoveHolidayTimeBoundBreakRule = (index) => {
    if (holidayTimeBoundBreakRules.length <= 1) {
      return;
    }
    markEarningInteraction();
    setHolidayTimeBoundBreakRules((prev) => prev.filter((_, i) => i !== index));
    setHolidayTimeBoundBreakErrors((prev) =>
      prev.filter((_, i) => i !== index),
    );
  };

  const handleHolidayTimeBoundBreakRuleChange = (index, field, value) => {
    markEarningInteraction();
    setHolidayTimeBoundBreakRules((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    setHolidayTimeBoundBreakErrors((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: false } : row)),
    );
  };

  const handleHolidayTimeBoundBreakTimeChange = (index, newValue) => {
    if (!newValue || !newValue.isValid()) {
      handleHolidayTimeBoundBreakRuleChange(index, "hours", "");
      handleHolidayTimeBoundBreakRuleChange(index, "minutes", "");
      return;
    }
    handleHolidayTimeBoundBreakRuleChange(index, "hours", newValue.hour());
    handleHolidayTimeBoundBreakRuleChange(index, "minutes", newValue.minute());
  };

  const validateHolidayTimeBoundBreakRules = () => {
    if (!watch("is_holiday_time_bound_break_deduction_allowed")) {
      setHolidayTimeBoundBreakErrors([]);
      return true;
    }

    const nextErrors = validateTimeBoundBreakRuleRows(
      holidayTimeBoundBreakRules,
    );
    setHolidayTimeBoundBreakErrors(nextErrors);
    return !hasTimeBoundBreakErrors(nextErrors);
  };

  const onSubmit = async (formValues) => {
    const isValid =
      watch("is_overtime_allowed") && watch("calculation_based_on") === "Shift"
        ? validateTableRef.current()
        : true;
    const isWeekoffValid =
      watch("payment_for_week_off_attendance") &&
      watch("weekoff_calculation_based_on") === "Shift"
        ? validateTableRefWeekoff.current()
        : true;

    const isHolidayValid =
      watch("is_payment_for_holiday_day_attendance") &&
      watch("holiday_calculation_based_on") === "Shift"
        ? validateTableRefHoliday.current()
        : true;
    const isOtPaymentFactorValid = validateOtPaymentFactorRows();
    const isOtEquivalentHoursValid = validateOtEquivalentHoursTable();
    const isWeekoffPaymentFactorValid = validateWeekoffPaymentFactorRows();
    const isHolidayPaymentFactorValid = validateHolidayPaymentFactorRows();
    const isWeekoffTimeBoundBreakValid = validateWeekoffTimeBoundBreakRules();
    const isHolidayTimeBoundBreakValid = validateHolidayTimeBoundBreakRules();
    if (
      isValid &&
      isWeekoffValid &&
      isHolidayValid &&
      isOtPaymentFactorValid &&
      isOtEquivalentHoursValid &&
      isWeekoffPaymentFactorValid &&
      isHolidayPaymentFactorValid &&
      isWeekoffTimeBoundBreakValid &&
      isHolidayTimeBoundBreakValid
    ) {
      const otRows = getConfiguredPaymentFactorRows(
        getConfiguredPaymentFactorRows(otPaymentFactorRows).length > 0
          ? otPaymentFactorRows
          : formValues.ot_payment_factor_configurations,
      );
      const weekoffRowsConfig = getConfiguredPaymentFactorRows(
        getConfiguredPaymentFactorRows(weekoffPaymentFactorRows).length > 0
          ? weekoffPaymentFactorRows
          : formValues.weekoff_payment_factor_configurations,
      );
      const holidayRowsConfig = getConfiguredPaymentFactorRows(
        getConfiguredPaymentFactorRows(holidayPaymentFactorRows).length > 0
          ? holidayPaymentFactorRows
          : formValues.holiday_payment_factor_configurations,
      );
      const otEquivalentRows = getConfiguredEquivalentHoursRows(
        getConfiguredEquivalentHoursRows(otEquivalentHoursRows).length > 0
          ? otEquivalentHoursRows
          : formValues.ot_equivalent_hours_configurations,
      );
      const weekoffTimeBoundBreakRulesConfig = getConfiguredTimeBoundBreakRows(
        getConfiguredTimeBoundBreakRows(weekoffTimeBoundBreakRules).length > 0
          ? weekoffTimeBoundBreakRules
          : formValues.weekoff_time_bound_break_deduction_configurations,
      );
      const holidayTimeBoundBreakRulesConfig = getConfiguredTimeBoundBreakRows(
        getConfiguredTimeBoundBreakRows(holidayTimeBoundBreakRules).length > 0
          ? holidayTimeBoundBreakRules
          : formValues.holiday_time_bound_break_deduction_configurations,
      );

      onUpdate({
        ...formValues,
        rows,
        weekoffRows,
        holidayRows,
        ot_equivalent_hours_configurations: otEquivalentRows,
        ot_payment_factor_configurations: otRows,
        weekoff_payment_factor_configurations: weekoffRowsConfig,
        holiday_payment_factor_configurations: holidayRowsConfig,
        weekoff_time_bound_break_deduction_configurations:
          weekoffTimeBoundBreakRulesConfig,
        holiday_time_bound_break_deduction_configurations:
          holidayTimeBoundBreakRulesConfig,
      });
      handleNext(2);
    }
  };

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  useEffect(() => {
    if (!hasInitializedData.current && hasInitialEarningData(data)) {
      hasInitializedData.current = true;
      skipNextPaymentFactorPersist.current = true;
      skipNextWeekoffTimeBoundBreakPersist.current = true;
      skipNextHolidayTimeBoundBreakPersist.current = true;
      reset(data);
      const mappedRows = Array.isArray(data?.rows)
        ? data.rows.map((item) => ({
            work_min_percentage: item.work_min_percentage,
            count_as_shift: item.count_as_shift,
          }))
        : [{ work_min_percentage: "", count_as_shift: "" }];
      const mappedweekoffRows = Array.isArray(data?.weekoffRows)
        ? data.weekoffRows.map((item) => ({
            work_min_percentage: item.work_min_percentage,
            count_as_shift: item.count_as_shift,
          }))
        : [{ work_min_percentage: "", count_as_shift: "" }];
      const mappedholidayRows = Array.isArray(data?.holidayRows)
        ? data.holidayRows.map((item) => ({
            work_min_percentage: item.work_min_percentage,
            count_as_shift: item.count_as_shift,
          }))
        : [{ work_min_percentage: "", count_as_shift: "" }];
      setHolidayRows(mappedholidayRows);
      setRows(mappedRows);
      setWeekoffRows(mappedweekoffRows);
      const mappedPaymentFactorRows = mapPaymentFactorRows(
        data?.ot_payment_factor_configurations,
      );
      const mappedWeekoffPaymentFactorRows = mapPaymentFactorRows(
        data?.weekoff_payment_factor_configurations,
      );
      const mappedHolidayPaymentFactorRows = mapPaymentFactorRows(
        data?.holiday_payment_factor_configurations,
      );
      const mappedEquivalentHoursRows = Array.isArray(
        data?.ot_equivalent_hours_configurations,
      )
        ? data.ot_equivalent_hours_configurations.map((item) => ({
            min_hours: normalizeEquivalentHoursTime(item?.min_hours ?? ""),
            credited_hours: normalizeEquivalentHoursTime(
              item?.credited_hours ?? "",
            ),
          }))
        : [];
      const restoredOtPaymentFactorRows = mappedPaymentFactorRows;
      const restoredWeekoffPaymentFactorRows = mappedWeekoffPaymentFactorRows;
      const restoredHolidayPaymentFactorRows = mappedHolidayPaymentFactorRows;
      setOtPaymentFactorRows(
        restoredOtPaymentFactorRows.length > 0
          ? restoredOtPaymentFactorRows
          : [createEmptyPaymentFactorRow()],
      );
      setValue(
        "ot_payment_factor_configurations",
        getConfiguredPaymentFactorRows(restoredOtPaymentFactorRows),
        { shouldValidate: false },
      );
      setWeekoffPaymentFactorRows(
        restoredWeekoffPaymentFactorRows.length > 0
          ? restoredWeekoffPaymentFactorRows
          : [createEmptyPaymentFactorRow()],
      );
      setValue(
        "weekoff_payment_factor_configurations",
        getConfiguredPaymentFactorRows(restoredWeekoffPaymentFactorRows),
        { shouldValidate: false },
      );
      setHolidayPaymentFactorRows(
        restoredHolidayPaymentFactorRows.length > 0
          ? restoredHolidayPaymentFactorRows
          : [createEmptyPaymentFactorRow()],
      );
      setValue(
        "holiday_payment_factor_configurations",
        getConfiguredPaymentFactorRows(restoredHolidayPaymentFactorRows),
        { shouldValidate: false },
      );
      setOtEquivalentHoursRows(
        mappedEquivalentHoursRows.length > 0
          ? mappedEquivalentHoursRows
          : [createEmptyEquivalentHoursRow()],
      );
      setOtEquivalentHoursErrors(
        mappedEquivalentHoursRows.length > 0
          ? validateEquivalentHoursRows(mappedEquivalentHoursRows)
          : [{ min_hours: false, credited_hours: false }],
      );
      setValue(
        "ot_equivalent_hours_configurations",
        getConfiguredEquivalentHoursRows(mappedEquivalentHoursRows),
        { shouldValidate: false },
      );
      const mappedWeekoffTimeBoundBreakRules = mapTimeBoundBreakConfigRows(
        data?.weekoff_time_bound_break_deduction_configurations,
      );
      setWeekoffTimeBoundBreakRules(mappedWeekoffTimeBoundBreakRules);
      setWeekoffTimeBoundBreakErrors(
        mappedWeekoffTimeBoundBreakRules.map(() => ({
          hours: false,
          minutes: false,
          deduct_minutes: false,
        })),
      );
      setValue(
        "weekoff_time_bound_break_deduction_configurations",
        mappedWeekoffTimeBoundBreakRules,
        { shouldValidate: false },
      );
      const mappedHolidayTimeBoundBreakRules = mapTimeBoundBreakConfigRows(
        data?.holiday_time_bound_break_deduction_configurations,
      );
      setHolidayTimeBoundBreakRules(mappedHolidayTimeBoundBreakRules);
      setHolidayTimeBoundBreakErrors(
        mappedHolidayTimeBoundBreakRules.map(() => ({
          hours: false,
          minutes: false,
          deduct_minutes: false,
        })),
      );
      setValue(
        "holiday_time_bound_break_deduction_configurations",
        mappedHolidayTimeBoundBreakRules,
        { shouldValidate: false },
      );
    }
  }, [
    createEmptyEquivalentHoursRow,
    createEmptyPaymentFactorRow,
    data,
    getConfiguredEquivalentHoursRows,
    getConfiguredPaymentFactorRows,
    mapPaymentFactorRows,
    mapTimeBoundBreakConfigRows,
    normalizeEquivalentHoursTime,
    reset,
    setValue,
    validateEquivalentHoursRows,
  ]);

  useEffect(() => {
    if (!hasMounted.current) {
      return;
    }

    if (skipNextPaymentFactorPersist.current) {
      skipNextPaymentFactorPersist.current = false;
      return;
    }

    const currentOt = getConfiguredPaymentFactorRows(otPaymentFactorRows);
    const currentWeekoff = getConfiguredPaymentFactorRows(
      weekoffPaymentFactorRows,
    );
    const currentHoliday = getConfiguredPaymentFactorRows(
      holidayPaymentFactorRows,
    );

    const nextOt = JSON.stringify(currentOt);
    const nextWeekoff = JSON.stringify(currentWeekoff);
    const nextHoliday = JSON.stringify(currentHoliday);
    const currentEquivalent = getConfiguredEquivalentHoursRows(
      otEquivalentHoursRows,
    );
    const nextEquivalent = JSON.stringify(currentEquivalent);

    if (
      nextOt !== lastPersistedPaymentFactors.current.ot ||
      nextWeekoff !== lastPersistedPaymentFactors.current.weekoff ||
      nextHoliday !== lastPersistedPaymentFactors.current.holiday ||
      nextEquivalent !== lastPersistedPaymentFactors.current.equivalent
    ) {
      lastPersistedPaymentFactors.current = {
        ot: nextOt,
        weekoff: nextWeekoff,
        holiday: nextHoliday,
        equivalent: nextEquivalent,
      };

      onUpdate({
        ot_equivalent_hours_configurations: currentEquivalent,
        ot_payment_factor_configurations: currentOt,
        weekoff_payment_factor_configurations: currentWeekoff,
        holiday_payment_factor_configurations: currentHoliday,
      });
    }
  }, [
    getConfiguredPaymentFactorRows,
    holidayPaymentFactorRows,
    onUpdate,
    otEquivalentHoursRows,
    otPaymentFactorRows,
    weekoffPaymentFactorRows,
    getConfiguredEquivalentHoursRows,
  ]);

  useEffect(() => {
    if (!hasMounted.current) {
      return;
    }

    if (skipNextWeekoffTimeBoundBreakPersist.current) {
      skipNextWeekoffTimeBoundBreakPersist.current = false;
      return;
    }

    const currentWeekoffTimeBoundBreak = getConfiguredTimeBoundBreakRows(
      weekoffTimeBoundBreakRules,
    );
    const nextWeekoffTimeBoundBreak = JSON.stringify(
      currentWeekoffTimeBoundBreak,
    );

    if (
      nextWeekoffTimeBoundBreak !== lastPersistedWeekoffTimeBoundBreak.current
    ) {
      lastPersistedWeekoffTimeBoundBreak.current = nextWeekoffTimeBoundBreak;
      setValue(
        "weekoff_time_bound_break_deduction_configurations",
        currentWeekoffTimeBoundBreak,
        { shouldValidate: false, shouldDirty: true },
      );
      onUpdate({
        weekoff_time_bound_break_deduction_configurations:
          currentWeekoffTimeBoundBreak,
      });
    }
  }, [
    getConfiguredTimeBoundBreakRows,
    onUpdate,
    setValue,
    weekoffTimeBoundBreakRules,
  ]);

  useEffect(() => {
    if (!hasMounted.current) {
      return;
    }

    if (skipNextHolidayTimeBoundBreakPersist.current) {
      skipNextHolidayTimeBoundBreakPersist.current = false;
      return;
    }

    const currentHolidayTimeBoundBreak = getConfiguredTimeBoundBreakRows(
      holidayTimeBoundBreakRules,
    );
    const nextHolidayTimeBoundBreak = JSON.stringify(
      currentHolidayTimeBoundBreak,
    );

    if (
      nextHolidayTimeBoundBreak !== lastPersistedHolidayTimeBoundBreak.current
    ) {
      lastPersistedHolidayTimeBoundBreak.current = nextHolidayTimeBoundBreak;
      setValue(
        "holiday_time_bound_break_deduction_configurations",
        currentHolidayTimeBoundBreak,
        { shouldValidate: false, shouldDirty: true },
      );
      onUpdate({
        holiday_time_bound_break_deduction_configurations:
          currentHolidayTimeBoundBreak,
      });
    }
  }, [
    getConfiguredTimeBoundBreakRows,
    onUpdate,
    setValue,
    holidayTimeBoundBreakRules,
  ]);

  useEffect(() => {
    if (
      !Array.isArray(otEquivalentHoursRows) ||
      otEquivalentHoursRows.length === 0
    ) {
      setOtEquivalentHoursRows([createEmptyEquivalentHoursRow()]);
    }
  }, [createEmptyEquivalentHoursRow, otEquivalentHoursRows]);

  useEffect(() => {
    if (
      watch("is_overtime_allowed") &&
      watch("calculation_based_on") === "Hours" &&
      watch("is_ot_payment_based_on") === "Attendance" &&
      otPaymentFactorRows.length > 0
    ) {
      setOtPaymentFactorErrors(getOtPaymentFactorErrors(otPaymentFactorRows));
    }
  }, [getOtPaymentFactorErrors, otPaymentFactorRows, watch]);

  useEffect(() => {
    if (
      watch("is_overtime_allowed") &&
      watch("calculation_based_on") === "Hours" &&
      watch("is_ot_payment_based_on") === "Equivalent Hours"
    ) {
      if (skipNextEquivalentHoursValidation.current) {
        skipNextEquivalentHoursValidation.current = false;
        return;
      }

      if (!hasEquivalentHoursErrors(otEquivalentHoursErrors)) {
        return;
      }

      setOtEquivalentHoursErrors(
        validateEquivalentHoursRows(otEquivalentHoursRows),
      );
    }
  }, [
    hasEquivalentHoursErrors,
    otEquivalentHoursErrors,
    otEquivalentHoursRows,
    validateEquivalentHoursRows,
    watch,
  ]);

  useEffect(() => {
    setValue(
      "ot_equivalent_hours_configurations",
      getConfiguredEquivalentHoursRows(otEquivalentHoursRows),
      { shouldValidate: false },
    );
  }, [getConfiguredEquivalentHoursRows, otEquivalentHoursRows, setValue]);

  useEffect(() => {
    if (clearAbsenceValues) {
      setValue("is_absence_paid", false);
      setValue("absent_payment_based_on", "Paid");
      setClearAbsenceValues(false);
    }
  });

  const resetHolidayPaymentFields = () => {
    const fields = [
      "holiday_attendance_payment_factor",
      "holiday_attendance_fixed_amount",
    ];
    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("is_holiday_payment_based_on", "Attendance");
    clearPaymentFactorRows("holiday");
    turnOffHolidayTimeBoundBreak();
  };

  // Time-Bound Break Deductions sit under the payment toggle: when that
  // toggle is switched off / hidden, the break toggle goes back to off (and
  // its rules are cleared) so it doesn't reopen as on.
  const turnOffWeekoffTimeBoundBreak = () => {
    setValue("is_weekoff_time_bound_break_deduction_allowed", false);
    resetWeekoffTimeBoundBreakRules();
  };
  const turnOffHolidayTimeBoundBreak = () => {
    setValue("is_holiday_time_bound_break_deduction_allowed", false);
    resetHolidayTimeBoundBreakRules();
  };

  const resetWeekOffFields = () => {
    const fields = [
      "weekoff_attendance_payment_factor",
      "weekoff_attendance_fixed_amount",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("is_weekoff_payment_based_on", "Attendance");
    clearPaymentFactorRows("weekoff");
    turnOffWeekoffTimeBoundBreak();
  };

  const resetOtFixedAmount = () => {
    const fields = [
      "ot_fixed_amount_thereshold_limit",
      "ot_fixed_amount_payable",
      "ot_fixed_amount_additional_mins",
      "ot_fixed_amount_additional_payable",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("fixed_amount_deduct_type", "");
  };

  const resetHolidayFixedAmount = () => {
    const fields = [
      "holiday_fixed_amount_thres_hold",
      "holiday_fixed_amount_payable",
      "holiday_fixed_amount_additional_mins",
      "holiday_fixed_amount_additional_payable",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
  };

  const resetWeekoffFixedAmount = () => {
    const fields = [
      "weekoff_additional_thres_hold",
      "weekoff_additional_thres_hold_consideration",
      "weekoff_fixed_amount_thres_hold",
      "weekoff_fixed_amount_thres_hold_consideration",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
  };

  const resetHolidayAttendance = () => {
    const fields = [
      "holiday_thres_hold_limit",
      "holiday_thres_hold_consideration",
      "holiday_additional_thres_hold",
      "holiday_additional_thres_hold_consideration",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    clearPaymentFactorRows("holiday");
  };

  const resetWeekoffAttendance = () => {
    const fields = [
      "weekoff_thres_hold_limit",
      "weekoff_thres_hold_consideration",
      "weekoff_additional_thres_hold",
      "weekoff_additional_thres_hold_consideration",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("attendance_deduct_type", "");
    clearPaymentFactorRows("weekoff");
  };

  const resetOtAttendance = () => {
    const fields = [
      "minimum_ot_consideration",
      "minutes_for_consideration",
      "additional_minutes_for_consideration",
      "additional_ot",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setValue("attendance_deduct_type", "");
    clearPaymentFactorRows("ot");
  };

  const resetOtEquivalentHours = () => {
    const fields = [
      "equivalent_hours_additional_minutes",
      "equivalent_hours_ot_minutes",
    ];

    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setOtEquivalentHoursRows([createEmptyEquivalentHoursRow()]);
    setOtEquivalentHoursErrors([{ min_hours: false, credited_hours: false }]);
    setValue("ot_equivalent_hours_configurations", [], {
      shouldValidate: false,
      shouldDirty: true,
    });
  };

  const resetOtSftTable = () => {
    const fields = [
      "deduct_additional_percentage",
      "deduct_shift_count",
      "ot_sft_payment_factor",
    ];
    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setRows([{ work_min_percentage: "", count_as_shift: "" }]);
    setRowError([{ work_min_percentage: false, count_as_shift: false }]);
  };

  const resetWeekoffSftTable = () => {
    const fields = [
      "weekoff_partial_shift_additional_percentage",
      "weekoff_partial_shift_count",
      "week_off_payment_factor",
    ];
    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });
    setWeekoffRows([{ work_min_percentage: "", count_as_shift: "" }]);
    setWeekoffRowError([{ work_min_percentage: false, count_as_shift: false }]);
  };

  const resetHolidaySftTable = () => {
    const fields = [
      "holiday_partial_shift_additional_percentage",
      "holiday_partial_shift_count",
      "holiday_sft_payment_factor",
    ];
    fields.forEach((field) => {
      resetField(field, { keepError: false });
      setValue(field, 0, { shouldValidate: false });
    });

    setHolidayRows([{ work_min_percentage: "", count_as_shift: "" }]);
    setHolidayRowError([{ work_min_percentage: false, count_as_shift: false }]);
  };

  const resetOtData = () => {
    setValue("ot_work_preauthorization", "Automatic");
    setValue("ot_timing_privilege", "Only Before");
    setValue("calculation_based_on", "Hours");
    setValue("is_ot_payment_based_on", "Attendance");
    setValue("ot_equivalent_hours_configurations", []);
    clearPaymentFactorRows("ot");
  };

  const renderPaymentFactorSummary = (type) => {
    const config = paymentFactorConfig[type];
    const configuredRows = getConfiguredPaymentFactorRows(config.rows);

    console.log(configuredRows);

    return (
      <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography fontWeight="medium">Payment Factor</Typography>
            <Typography variant="body2" color="text.secondary">
              {config.description}
            </Typography>
          </Box>
          <CustomButton
            actionFuntion={() => handleOpenPaymentFactorDialog(type)}
            actionName="Add"
            typeName="button"
          />
        </Box>
        <Table
          size="small"
          sx={{
            mt: 1,
            border: `1px solid ${themes?.miliedLightGray}`,
            "& th": {
              backgroundColor: themes?.primary,
              fontWeight: 600,
              textAlign: "left",
              border: `1px solid ${themes?.miliedLightGray}`,
            },
            "& td": {
              border: `1px solid ${themes?.miliedLightGray}`,
              textAlign: "left",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>Work Band Name</TableCell>
              <TableCell rowSpan={2}>Factor</TableCell>
              <TableCell
                colSpan={2}
                align="center"
                sx={{ textAlign: "center !important" }}
              >
                Time Band
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bandOptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    {config.emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : configuredRows.length > 0 ? (
              configuredRows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2">
                      {row.band_name || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.factor || "-"}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.start_time || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.end_time || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell>
                  <Typography variant="body2">-</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">-</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">-</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">-</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {config.errors.some((row) => row.range) && (
          <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
            Time bands must be within 00:00 to 24:00, non-overlapping, and end
            time must be after start time.
          </Typography>
        )}
      </Box>
    );
  };

  const renderNonOtPaymentFactorDialog = () => {
    if (paymentFactorDialogType === "ot") return null;

    const config = paymentFactorConfig[paymentFactorDialogType];

    return (
      <Dialog
        open={openPaymentFactorDialog}
        onClose={handleClosePaymentFactorDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>{config.title}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {config.dialogDescription}
            </Typography>

            <CustomButton
              actionName="Add Row"
              typeName="button"
              actionFuntion={handleAddPaymentFactorRow}
            />
          </Box>
          <Table
            size="small"
            sx={{
              border: `1px solid ${themes?.miliedLightGray}`,
              "& th": {
                backgroundColor: themes?.primary,
                fontWeight: 600,
                textAlign: "center",
                border: `1px solid ${themes?.miliedLightGray}`,
              },
              "& td": {
                border: `1px solid ${themes?.miliedLightGray}`,
                textAlign: "center",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2}>Work Band Name</TableCell>
                <TableCell rowSpan={2}>Factor</TableCell>
                <TableCell colSpan={2}>Time Band</TableCell>
                <TableCell rowSpan={2}>Actions</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {otPaymentFactorDraftRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      No payment factor rows added yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                otPaymentFactorDraftRows.map((row, index) => {
                  const rowError = otPaymentFactorDraftErrors[index] ?? {};
                  const isRowEditing = row.isEditing ?? false;

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        {isRowEditing ? (
                          <FormControl
                            fullWidth
                            size="small"
                            error={Boolean(rowError.overtime_band)}
                          >
                            <Select
                              value={row.overtime_band ?? ""}
                              onChange={(event) =>
                                handlePaymentFactorDraftBandChange(
                                  index,
                                  event.target.value,
                                )
                              }
                              displayEmpty
                            >
                              <MenuItem value="" disabled>
                                Select band
                              </MenuItem>
                              {getPaymentFactorBandOptionsForRow(
                                otPaymentFactorDraftRows,
                                index,
                              ).map((band) => (
                                <MenuItem key={band.id} value={band.id}>
                                  {band.band_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2">
                            {row.band_name || "-"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isRowEditing ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={row.factor ?? ""}
                            inputProps={{ style: { textAlign: "center" } }}
                            disabled
                          />
                        ) : (
                          <Typography variant="body2">
                            {row.factor || "-"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isRowEditing ? (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={["TimePicker"]}>
                              <div sx={{ width: 4 }}>
                                <TimePicker
                                  ampm={false}
                                  viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                  }}
                                  value={getTimePickerValue(row.start_time)}
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      width: 15,
                                      error: Boolean(
                                        rowError.start_time || rowError.range,
                                      ),
                                      helperText:
                                        getPaymentFactorTimeHelperText(
                                          row,
                                          rowError,
                                          "start_time",
                                        ),
                                      sx: {
                                        textAlign: "center",
                                        "& .MuiOutlinedInput-root": {
                                          "&:hover fieldset": {
                                            borderColor: "green",
                                          },
                                        },
                                      },
                                    },
                                  }}
                                  onChange={(newValue) =>
                                    handlePaymentFactorDraftTimeChange(
                                      index,
                                      "start_time",
                                      newValue,
                                    )
                                  }
                                />
                              </div>
                            </DemoContainer>
                          </LocalizationProvider>
                        ) : (
                          <Typography variant="body2">
                            {row.start_time || "-"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {isRowEditing ? (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={["TimePicker"]}>
                              <div sx={{ width: 4 }}>
                                <TimePicker
                                  ampm={false}
                                  viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                  }}
                                  value={getTimePickerValue(row.end_time)}
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      width: 15,
                                      error: Boolean(
                                        rowError.end_time || rowError.range,
                                      ),
                                      helperText:
                                        getPaymentFactorTimeHelperText(
                                          row,
                                          rowError,
                                          "end_time",
                                        ),
                                      sx: {
                                        textAlign: "center",
                                        "& .MuiOutlinedInput-root": {
                                          "&:hover fieldset": {
                                            borderColor: "green",
                                          },
                                        },
                                      },
                                    },
                                  }}
                                  onChange={(newValue) =>
                                    handlePaymentFactorDraftTimeChange(
                                      index,
                                      "end_time",
                                      newValue,
                                    )
                                  }
                                />
                              </div>
                            </DemoContainer>
                          </LocalizationProvider>
                        ) : (
                          <Typography variant="body2">
                            {row.end_time || "-"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          {isRowEditing ? (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                handleSavePaymentFactorDraftRow(index)
                              }
                            >
                              <Check fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                handleEditPaymentFactorDraftRow(index)
                              }
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDeletePaymentFactorDraftRow(index)
                            }
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {otPaymentFactorDraftErrors.some((row) => row.range) && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Time bands must not overlap and must cover the full 24-hour period
              without any gaps.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton
            actionName="Cancel"
            typeName="button"
            actionFuntion={handleClosePaymentFactorDialog}
          />

          <CustomButton
            actionName="Done"
            typeName="button"
            actionFuntion={() => {
              const nextErrors = getOtPaymentFactorErrors(
                otPaymentFactorDraftRows,
              );
              setOtPaymentFactorDraftErrors(nextErrors);
              if (!hasOtPaymentFactorErrors(nextErrors)) {
                const committedRows = otPaymentFactorDraftRows.map((row) => ({
                  ...row,
                  isEditing: false,
                }));
                syncPaymentFactorRows(paymentFactorDialogType, committedRows);
                setOtPaymentFactorDraftRows([]);
                setOtPaymentFactorDraftErrors([]);
                setOpenPaymentFactorDialog(false);
              }
            }}
          />
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box sx={mainOptions}>
            <Box sx={boxStyle(watch("is_overtime_allowed"))}>
              <Typography fontWeight="medium">Is OverTime Allowed</Typography>

              <Switch
                checked={watch("is_overtime_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_overtime_allowed", checked);
                  if (!checked) {
                    resetOtData();
                    resetOtSftTable();
                    resetOtAttendance();
                    resetOtFixedAmount();
                    resetOtEquivalentHours();
                  }
                }}
              />
            </Box>
            {watch("is_overtime_allowed") && (
              <>
                <Box sx={{ ml: 2, mb: 2 }}>
                  <Typography fontWeight="medium">
                    OT work Preauthorization
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Define whether overtime is auto-approved or requires manual
                    approval.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("ot_work_preauthorization")}
                    onChange={handleOTPreauthorization}
                    sx={optionsStyle}
                  >
                    {otPreauthorizationOptions.map((opt) => {
                      const isActive =
                        watch("ot_work_preauthorization") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                {watch("ot_work_preauthorization") === "Automatic" && (
                  <>
                    <Box sx={{ ml: 2, mb: 2 }}>
                      <Typography fontWeight="medium">
                        OT timing privilege
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Specify when overtime hours count toward payment
                        calculation.
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <RadioGroup
                        row
                        value={watch("ot_timing_privilege")}
                        onChange={hanldeOTPrivilege}
                        sx={optionsStyle}
                      >
                        {otTimingOptions.map((opt) => {
                          const isActive =
                            watch("ot_timing_privilege") === opt.id;
                          return (
                            <Box key={opt.id} sx={cardStyle(isActive)}>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Radio value={opt.id} />
                                <Typography
                                  fontWeight="medium"
                                  sx={{
                                    color: isActive
                                      ? themes.stepActiveColor
                                      : "inherit",
                                  }}
                                >
                                  {opt.label}
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  ml: 4,
                                  maxWidth: 250,
                                  wordBreak: "break-word",
                                }}
                              >
                                {opt.description}
                              </Typography>
                            </Box>
                          );
                        })}
                      </RadioGroup>
                    </Box>
                  </>
                )}
                <Box sx={{ ml: 2, mb: 2 }}>
                  <Typography fontWeight="medium">
                    Calculation based on
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose whether overtime is calculated based on hours worked
                    or entire shifts.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("calculation_based_on")}
                    onChange={hanldeOTCalculationBasedOn}
                    sx={optionsStyle}
                  >
                    {calculationOptions.map((opt) => {
                      const isActive = watch("calculation_based_on") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                          {opt.id === "Shift" &&
                            watch("calculation_based_on") === "Shift" && (
                              <Box sx={{ ...amtFactorStyle, mt: 0.5 }}>
                                <Typography fontWeight="medium">
                                  Payment Factor{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="ot_sft_payment_factor"
                                  type="shift"
                                  value={watch("ot_sft_payment_factor")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue("ot_sft_payment_factor", value);
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                {watch("calculation_based_on") === "Shift" && (
                  <>
                    <Box sx={{ px: 2, py: 1, mb: 2 }}>
                      <ShiftWorkTable
                        rows={rows}
                        setRows={setRows}
                        rowError={rowError}
                        setRowError={setRowError}
                        onValidateRef={validateTableRef}
                      />
                    </Box>
                    <Box sx={additionalInfoStyle}>
                      <Box sx={individualBoxStyle}>
                        <Typography variant="body2" color="text.secondary">
                          Each additional
                        </Typography>
                        <CustomBox
                          name="deduct_additional_percentage"
                          type="percentage"
                          value={watch("deduct_additional_percentage")}
                          register={register}
                          errors={errors}
                          handleChange={(e, value) => {
                            setValue("deduct_additional_percentage", value);
                          }}
                          rules={{ required: "Enter value" }}
                          inputWidth="50px"
                        />

                        <Typography variant="body2" color="text.secondary">
                          percentage is counted as
                        </Typography>
                        <CustomBox
                          name="deduct_shift_count"
                          type="shift"
                          value={watch("deduct_shift_count")}
                          register={register}
                          errors={errors}
                          handleChange={(e, value) => {
                            setValue("deduct_shift_count", value);
                          }}
                          rules={{ required: "Enter value" }}
                          inputWidth="50px"
                        />

                        <Typography variant="body2" color="text.secondary">
                          shift
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={infoMessageStyle}>
                      <ShiftWorkExample />
                    </Box>
                  </>
                )}

                {watch("calculation_based_on") === "Hours" && (
                  <>
                    <Box sx={{ ml: 2, mb: 2 }}>
                      <Typography fontWeight="medium">
                        Payment Based On
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Decide whether payment is linked to attendance factor or
                        a fixed amount
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <RadioGroup
                        row
                        value={watch("is_ot_payment_based_on")}
                        onChange={handleOTPaymentBasedOn}
                        sx={optionsStyle}
                      >
                        {otPaymentOptions.map((opt) => {
                          const isActive =
                            watch("is_ot_payment_based_on") === opt.id;

                          return (
                            <Box key={opt.id} sx={cardStyle(isActive)}>
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Radio value={opt.id} />

                                <Typography
                                  fontWeight="medium"
                                  sx={{
                                    color: isActive
                                      ? themes.stepActiveColor
                                      : "inherit",
                                  }}
                                >
                                  {opt.label}
                                </Typography>
                              </Box>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  ml: 4,
                                  maxWidth: 250,
                                  wordBreak: "break-word",
                                }}
                              >
                                {opt.description}
                              </Typography>
                            </Box>
                          );
                        })}
                      </RadioGroup>
                    </Box>
                    {watch("is_ot_payment_based_on") === "Attendance" ||
                    watch("is_ot_payment_based_on") === "Equivalent Hours" ? (
                      <>
                        {isBandAllowed && (
                          <Box sx={{ ...switchStyle, mb: 1 }}>
                            <Box>
                              <Typography fontWeight="medium">
                                Is Overtime Band Factor Applied
                              </Typography>
                            </Box>
                            <Box sx={{ mr: 1.6 }}>
                              <Switch
                                checked={watch(
                                  "is_allow_payment_factor_for_ot",
                                )}
                                onChange={(event) => {
                                  const checked = event.target.checked;
                                  setValue(
                                    "is_allow_payment_factor_for_ot",
                                    checked,
                                  );
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                        {watch("is_allow_payment_factor_for_ot") && (
                          <>
                            <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                }}
                              >
                                <Box>
                                  <Typography fontWeight="medium">
                                    Payment Factor
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Choose how overtime payment is calculated
                                    based on work bands and applicable time
                                    duration.
                                  </Typography>
                                </Box>
                                <CustomButton
                                  actionFuntion={() =>
                                    handleOpenPaymentFactorDialog("ot")
                                  }
                                  actionName="Add"
                                  typeName="button"
                                />
                              </Box>
                              <Table
                                size="small"
                                sx={{
                                  mt: 1,
                                  border: `1px solid ${themes?.miliedLightGray}`,
                                  "& th": {
                                    backgroundColor: themes?.primary,
                                    fontWeight: 600,
                                    textAlign: "left",
                                    border: `1px solid ${themes?.miliedLightGray}`,
                                  },
                                  "& td": {
                                    border: `1px solid ${themes?.miliedLightGray}`,
                                    textAlign: "left",
                                  },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell rowSpan={2}>
                                      Work Band Name
                                    </TableCell>
                                    <TableCell rowSpan={2}>Factor</TableCell>
                                    <TableCell
                                      colSpan={2}
                                      align="center"
                                      sx={{ textAlign: "center !important" }}
                                    >
                                      Time Band
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {bandOptions.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={4}>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          No overtime bands configured.
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ) : getConfiguredPaymentFactorRows(
                                      otPaymentFactorRows,
                                    ).length > 0 ? (
                                    getConfiguredPaymentFactorRows(
                                      otPaymentFactorRows,
                                    ).map((row, index) => (
                                      <TableRow key={index}>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {row.band_name || "-"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {row.factor || "-"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {row.start_time || "-"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2">
                                            {row.end_time || "-"}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  ) : (
                                    <TableRow>
                                      <TableCell>
                                        <Typography variant="body2">
                                          -
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          -
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          -
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">
                                          -
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                              {otPaymentFactorErrors.some(
                                (row) => row.range,
                              ) && (
                                <Typography
                                  variant="body2"
                                  color="error"
                                  sx={{ mt: 0.5 }}
                                >
                                  Time bands must be within 00:00 to 24:00,
                                  non-overlapping, and together must complete
                                  one 24-hour cycle.
                                </Typography>
                              )}
                            </Box>
                          </>
                        )}
                        {watch("is_ot_payment_based_on") === "Attendance" ? (
                          <Box>
                            <Box sx={{ ...additionalInfoStyle }}>
                              <Box sx={individualBoxStyle}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  The threshold limit for overtime is
                                </Typography>
                                <CustomBox
                                  name="minimum_ot_consideration"
                                  type="minutes"
                                  value={watch("minimum_ot_consideration")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue("minimum_ot_consideration", value);
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  mins will be considered as
                                </Typography>
                                <CustomBox
                                  name="minutes_for_consideration"
                                  type="minutes"
                                  value={watch("minutes_for_consideration")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "minutes_for_consideration",
                                      value,
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  mins
                                </Typography>
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    width: 180,
                                    verticalAlign: "middle",
                                  }}
                                >
                                  <CustomAutoComplete
                                    dataList={sftType
                                      ?.filter(
                                        (obj) => obj.label !== "Per Cycle",
                                      )
                                      .map((obj) => ({
                                        ...obj,
                                        label: obj.label,
                                      }))}
                                    rules={{
                                      ...register("attendance_deduct_type", {
                                        required: false,
                                        validate: {
                                          minLengthNoSpaces: (value) => {
                                            if (
                                              watch("is_overtime_allowed") &&
                                              watch(
                                                "is_ot_payment_based_on",
                                              ) === "Attendance" &&
                                              watch("calculation_based_on") ===
                                                "Hours" &&
                                              watch(
                                                "attendance_deduct_type",
                                              ) === ""
                                            ) {
                                              return "Value is required";
                                            } else {
                                              return true;
                                            }
                                          },
                                        },
                                      }),
                                    }}
                                    selectedValue={watch(
                                      "attendance_deduct_type",
                                    )}
                                    updateDataFunction={(event, data) =>
                                      setValue(
                                        "attendance_deduct_type",
                                        data?.label,
                                      )
                                    }
                                    placeHolder="Select type"
                                    errorMessage={
                                      errors?.attendance_deduct_type?.message
                                        ? errors?.attendance_deduct_type
                                            ?.message
                                        : ""
                                    }
                                    textBoxError={
                                      errors?.attendance_deduct_type
                                        ? true
                                        : false
                                    }
                                  />
                                </Box>
                              </Box>
                            </Box>
                            <Box sx={additionalInfoStyle}>
                              <Box sx={individualBoxStyle}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  After that, every additional
                                </Typography>
                                <CustomBox
                                  name="additional_ot"
                                  type="minutes"
                                  value={watch("additional_ot")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue("additional_ot", value);
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  mins will be considered as
                                </Typography>
                                <CustomBox
                                  name="additional_minutes_for_consideration"
                                  type="minutes"
                                  value={watch(
                                    "additional_minutes_for_consideration",
                                  )}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "additional_minutes_for_consideration",
                                      value,
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  mins OT {watch("attendance_deduct_type")}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={infoMessageStyle}>
                              <PaymentBasedAttendanceExample />
                            </Box>
                            {watch("is_allow_payment_factor_for_ot") && (
                              <Box
                                sx={{
                                  ...infoMessageStyle,
                                  backgroundColor: "transparent",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                  }}
                                >
                                  <Checkbox
                                    checked={watch(
                                      "is_add_remaining_ot_to_base_factor",
                                    )}
                                    onChange={(event) =>
                                      setValue(
                                        "is_add_remaining_ot_to_base_factor",
                                        event.target.checked,
                                      )
                                    }
                                    sx={{ p: 0, mt: 0.3 }}
                                  />
                                  <Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography fontWeight="bold">
                                        Add remaining (unrounded) hours to 1.0
                                        OT
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      When OT hours are rounded (e.g., 1h 45m →
                                      1h 30m), the remaining 15 minutes will be
                                      added to payroll at 1.0 factor instead of
                                      being forfeited.
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box>
                                <Typography fontWeight="medium">
                                  Equivalent Hours Details
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Define how worked OT hours map to credited
                                  hours.
                                </Typography>
                              </Box>
                              <CustomButton
                                actionName="Add"
                                typeName="button"
                                actionFuntion={handleAddOtEquivalentHoursRow}
                              />
                            </Box>
                            <Table
                              size="small"
                              sx={{
                                border: `1px solid ${themes?.miliedLightGray}`,
                                "& th": {
                                  backgroundColor: "#f7f7fb",
                                  fontWeight: 600,
                                  border: `1px solid ${themes?.miliedLightGray}`,
                                },
                                "& td": {
                                  border: `1px solid ${themes?.miliedLightGray}`,
                                  verticalAlign: "top",
                                },
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell>Min hours</TableCell>
                                  <TableCell>Credited hrs</TableCell>
                                  <TableCell align="center">Action</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {otEquivalentHoursRows.map((row, index) => (
                                  <TableRow key={`ot-equivalent-${index}`}>
                                    <TableCell sx={{ minWidth: 180 }}>
                                      <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                      >
                                        <DemoContainer
                                          components={["TimePicker"]}
                                        >
                                          <div sx={{ width: 4 }}>
                                            <TimePicker
                                              ampm={false}
                                              viewRenderers={{
                                                hours: renderTimeViewClock,
                                                minutes: renderTimeViewClock,
                                                seconds: renderTimeViewClock,
                                              }}
                                              value={getTimePickerValue(
                                                row.min_hours,
                                              )}
                                              slotProps={{
                                                textField: {
                                                  fullWidth: true,
                                                  size: "small",
                                                  error: Boolean(
                                                    otEquivalentHoursErrors?.[
                                                      index
                                                    ]?.min_hours ||
                                                    otEquivalentHoursErrors?.[
                                                      index
                                                    ]?.range,
                                                  ),
                                                  helperText:
                                                    otEquivalentHoursErrors?.[
                                                      index
                                                    ]?.range
                                                      ? "Min hours already used"
                                                      : otEquivalentHoursErrors?.[
                                                            index
                                                          ]?.min_hours
                                                        ? "Select min hours"
                                                        : "",
                                                  sx: {
                                                    "& .MuiOutlinedInput-root":
                                                      {
                                                        "&:hover fieldset": {
                                                          borderColor: "green",
                                                        },
                                                      },
                                                  },
                                                },
                                              }}
                                              onChange={(newValue) =>
                                                handleOtEquivalentHoursChange(
                                                  index,
                                                  "min_hours",
                                                  newValue,
                                                )
                                              }
                                            />
                                          </div>
                                        </DemoContainer>
                                      </LocalizationProvider>
                                    </TableCell>
                                    <TableCell sx={{ minWidth: 180 }}>
                                      <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                      >
                                        <DemoContainer
                                          components={["TimePicker"]}
                                        >
                                          <div sx={{ width: 4 }}>
                                            <TimePicker
                                              ampm={false}
                                              viewRenderers={{
                                                hours: renderTimeViewClock,
                                                minutes: renderTimeViewClock,
                                                seconds: renderTimeViewClock,
                                              }}
                                              value={getTimePickerValue(
                                                row.credited_hours,
                                              )}
                                              slotProps={{
                                                textField: {
                                                  fullWidth: true,
                                                  size: "small",
                                                  error: Boolean(
                                                    otEquivalentHoursErrors?.[
                                                      index
                                                    ]?.credited_hours ||
                                                    otEquivalentHoursErrors?.[
                                                      index
                                                    ]?.range,
                                                  ),
                                                  helperText:
                                                    otEquivalentHoursErrors?.[
                                                      index
                                                    ]?.range
                                                      ? "Min hours already used"
                                                      : otEquivalentHoursErrors?.[
                                                            index
                                                          ]?.credited_hours
                                                        ? "Select credited hrs"
                                                        : "",
                                                  sx: {
                                                    "& .MuiOutlinedInput-root":
                                                      {
                                                        "&:hover fieldset": {
                                                          borderColor: "green",
                                                        },
                                                      },
                                                  },
                                                },
                                              }}
                                              onChange={(newValue) =>
                                                handleOtEquivalentHoursChange(
                                                  index,
                                                  "credited_hours",
                                                  newValue,
                                                )
                                              }
                                            />
                                          </div>
                                        </DemoContainer>
                                      </LocalizationProvider>
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{
                                        width: 80,
                                        textAlign: "center",
                                        verticalAlign: "middle",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          height: "100%",
                                        }}
                                      >
                                        <IconButton
                                          onClick={() =>
                                            handleDeleteOtEquivalentHoursRow(
                                              index,
                                            )
                                          }
                                          size="small"
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <Box
                              sx={{
                                border: `1px solid ${themes.miliedLightGray}`,
                                p: 1,
                                mb: 1,
                                mt: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  After that, every additional
                                </Typography>

                                <CustomBox
                                  name="equivalent_hours_additional_minutes"
                                  type="minutes"
                                  value={watch(
                                    "equivalent_hours_additional_minutes",
                                  )}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "equivalent_hours_additional_minutes",
                                      value,
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />

                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  mins will be considered as
                                </Typography>
                                <CustomBox
                                  name="equivalent_hours_ot_minutes"
                                  type="minutes"
                                  value={watch("equivalent_hours_ot_minutes")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue(
                                      "equivalent_hours_ot_minutes",
                                      value,
                                    );
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  mins OT
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}

                        <Dialog
                          open={
                            openPaymentFactorDialog &&
                            paymentFactorDialogType === "ot"
                          }
                          onClose={handleClosePaymentFactorDialog}
                          fullWidth
                          maxWidth="lg"
                        >
                          <DialogTitle>Payment Factor Details</DialogTitle>
                          <DialogContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Add, edit or remove overtime payment factor
                                rows.
                              </Typography>

                              <CustomButton
                                actionName="Add row"
                                typeName="button"
                                actionFuntion={handleAddPaymentFactorRow}
                              />
                            </Box>
                            <Table
                              size="small"
                              sx={{
                                border: `1px solid ${themes?.miliedLightGray}`,
                                "& th": {
                                  backgroundColor: themes?.primary,
                                  fontWeight: 600,
                                  textAlign: "center",
                                  border: `1px solid ${themes?.miliedLightGray}`,
                                },
                                "& td": {
                                  border: `1px solid ${themes?.miliedLightGray}`,
                                  textAlign: "center",
                                },
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell rowSpan={2}>
                                    Work Band Name
                                  </TableCell>
                                  <TableCell rowSpan={2}>Factor</TableCell>
                                  <TableCell colSpan={2}>Time Band</TableCell>
                                  <TableCell rowSpan={2}>Actions</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Start</TableCell>
                                  <TableCell>End</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {otPaymentFactorDraftRows.length === 0 ? (
                                  <TableRow>
                                    <TableCell colSpan={5}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        No payment factor rows added yet.
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ) : (
                                  otPaymentFactorDraftRows.map((row, index) => {
                                    const rowError =
                                      otPaymentFactorDraftErrors[index] ?? {};
                                    const isRowEditing = row.isEditing ?? false;
                                    return (
                                      <TableRow key={index}>
                                        <TableCell>
                                          {isRowEditing ? (
                                            <FormControl
                                              fullWidth
                                              size="small"
                                              error={Boolean(
                                                rowError.overtime_band,
                                              )}
                                            >
                                              <Select
                                                value={row.overtime_band ?? ""}
                                                onChange={(event) =>
                                                  handlePaymentFactorDraftBandChange(
                                                    index,
                                                    event.target.value,
                                                  )
                                                }
                                                displayEmpty
                                              >
                                                <MenuItem value="" disabled>
                                                  Select band
                                                </MenuItem>
                                                {getPaymentFactorBandOptionsForRow(
                                                  otPaymentFactorDraftRows,
                                                  index,
                                                ).map((band) => (
                                                  <MenuItem
                                                    key={band.id}
                                                    value={band.id}
                                                  >
                                                    {band.band_name}
                                                  </MenuItem>
                                                ))}
                                              </Select>
                                            </FormControl>
                                          ) : (
                                            <Typography variant="body2">
                                              {row.band_name || "-"}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {isRowEditing ? (
                                            <TextField
                                              fullWidth
                                              size="small"
                                              value={row.factor ?? ""}
                                              inputProps={{
                                                style: {
                                                  textAlign: "center",
                                                },
                                              }}
                                              disabled
                                            />
                                          ) : (
                                            <Typography variant="body2">
                                              {row.factor || "-"}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {isRowEditing ? (
                                            <LocalizationProvider
                                              dateAdapter={AdapterDayjs}
                                            >
                                              <DemoContainer
                                                components={["TimePicker"]}
                                              >
                                                <div sx={{ width: 4 }}>
                                                  <TimePicker
                                                    ampm={false}
                                                    viewRenderers={{
                                                      hours:
                                                        renderTimeViewClock,
                                                      minutes:
                                                        renderTimeViewClock,
                                                      seconds:
                                                        renderTimeViewClock,
                                                    }}
                                                    value={getTimePickerValue(
                                                      row.start_time,
                                                    )}
                                                    slotProps={{
                                                      textField: {
                                                        size: "small",
                                                        width: 15,
                                                        error: Boolean(
                                                          rowError.start_time ||
                                                          rowError.range,
                                                        ),
                                                        helperText:
                                                          getPaymentFactorTimeHelperText(
                                                            row,
                                                            rowError,
                                                            "start_time",
                                                          ),
                                                        sx: {
                                                          textAlign: "center",
                                                          "& .MuiOutlinedInput-root":
                                                            {
                                                              "&:hover fieldset":
                                                                {
                                                                  borderColor:
                                                                    "green",
                                                                },
                                                            },
                                                        },
                                                      },
                                                    }}
                                                    onChange={(newValue) =>
                                                      handlePaymentFactorDraftTimeChange(
                                                        index,
                                                        "start_time",
                                                        newValue,
                                                      )
                                                    }
                                                  />
                                                </div>
                                              </DemoContainer>
                                            </LocalizationProvider>
                                          ) : (
                                            <Typography variant="body2">
                                              {row.start_time || "-"}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {isRowEditing ? (
                                            <LocalizationProvider
                                              dateAdapter={AdapterDayjs}
                                            >
                                              <DemoContainer
                                                components={["TimePicker"]}
                                              >
                                                <div sx={{ width: 4 }}>
                                                  <TimePicker
                                                    ampm={false}
                                                    viewRenderers={{
                                                      hours:
                                                        renderTimeViewClock,
                                                      minutes:
                                                        renderTimeViewClock,
                                                      seconds:
                                                        renderTimeViewClock,
                                                    }}
                                                    value={getTimePickerValue(
                                                      row.end_time,
                                                    )}
                                                    slotProps={{
                                                      textField: {
                                                        size: "small",
                                                        width: 15,
                                                        error: Boolean(
                                                          rowError.end_time ||
                                                          rowError.range,
                                                        ),
                                                        helperText:
                                                          getPaymentFactorTimeHelperText(
                                                            row,
                                                            rowError,
                                                            "end_time",
                                                          ),
                                                        sx: {
                                                          textAlign: "center",
                                                          "& .MuiOutlinedInput-root":
                                                            {
                                                              "&:hover fieldset":
                                                                {
                                                                  borderColor:
                                                                    "green",
                                                                },
                                                            },
                                                        },
                                                      },
                                                    }}
                                                    onChange={(newValue) =>
                                                      handlePaymentFactorDraftTimeChange(
                                                        index,
                                                        "end_time",
                                                        newValue,
                                                      )
                                                    }
                                                  />
                                                </div>
                                              </DemoContainer>
                                            </LocalizationProvider>
                                          ) : (
                                            <Typography variant="body2">
                                              {row.end_time || "-"}
                                            </Typography>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              justifyContent: "center",
                                              gap: 0.5,
                                            }}
                                          >
                                            {isRowEditing ? (
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() =>
                                                  handleSavePaymentFactorDraftRow(
                                                    index,
                                                  )
                                                }
                                              >
                                                <Check fontSize="small" />
                                              </IconButton>
                                            ) : (
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() =>
                                                  handleEditPaymentFactorDraftRow(
                                                    index,
                                                  )
                                                }
                                              >
                                                <Edit fontSize="small" />
                                              </IconButton>
                                            )}
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() =>
                                                handleDeletePaymentFactorDraftRow(
                                                  index,
                                                )
                                              }
                                            >
                                              <Delete fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                            {otPaymentFactorDraftErrors.some(
                              (row) => row.range,
                            ) && (
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ mt: 1 }}
                              >
                                Time bands must use HH:mm, must not overlap, and
                                together must complete one 24-hour cycle without
                                gaps
                              </Typography>
                            )}
                          </DialogContent>
                          <DialogActions>
                            <CustomButton
                              actionName="Cancel"
                              typeName="button"
                              actionFuntion={handleClosePaymentFactorDialog}
                            />
                            <CustomButton
                              actionName="Done"
                              typeName="button"
                              actionFuntion={() => {
                                const nextErrors = getOtPaymentFactorErrors(
                                  otPaymentFactorDraftRows,
                                );
                                setOtPaymentFactorDraftErrors(nextErrors);
                                if (!hasOtPaymentFactorErrors(nextErrors)) {
                                  const committedRows =
                                    otPaymentFactorDraftRows.map((row) => ({
                                      ...row,
                                      isEditing: false,
                                    }));
                                  syncPaymentFactorRows("ot", committedRows);
                                  setOtPaymentFactorDraftRows([]);
                                  setOtPaymentFactorDraftErrors([]);
                                  setOpenPaymentFactorDialog(false);
                                }
                              }}
                            />
                          </DialogActions>
                        </Dialog>
                      </>
                    ) : (
                      <>
                        <Box sx={additionalInfoStyle}>
                          <Box sx={individualBoxStyle}>
                            <Typography variant="body2" color="text.secondary">
                              The threshold limit for ovetime is
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_thereshold_limit"
                              type="minutes"
                              value={watch("ot_fixed_amount_thereshold_limit")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue(
                                  "ot_fixed_amount_thereshold_limit",
                                  value,
                                );
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Typography variant="body2" color="text.secondary">
                              mins with an amount payable of
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_payable"
                              type="amount"
                              value={watch("ot_fixed_amount_payable")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue("ot_fixed_amount_payable", value);
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                width: 180,
                                verticalAlign: "middle",
                              }}
                            >
                              <CustomAutoComplete
                                dataList={sftType?.map((obj) => ({
                                  ...obj,
                                  label: obj?.label,
                                }))}
                                rules={{
                                  ...register("fixed_amount_deduct_type", {
                                    required: false,
                                    validate: {
                                      minLengthNoSpaces: (value) => {
                                        if (
                                          watch("is_overtime_allowed") &&
                                          watch("is_ot_payment_based_on") ===
                                            "Amount" &&
                                          watch("calculation_based_on") ===
                                            "Hours" &&
                                          watch("fixed_amount_deduct_type") ===
                                            ""
                                        ) {
                                          return "Value is required";
                                        } else {
                                          return true;
                                        }
                                      },
                                    },
                                  }),
                                }}
                                selectedValue={watch(
                                  "fixed_amount_deduct_type",
                                )}
                                updateDataFunction={(event, data) =>
                                  setValue(
                                    "fixed_amount_deduct_type",
                                    data?.label,
                                  )
                                }
                                placeHolder="Select type"
                                errorMessage={
                                  errors?.fixed_amount_deduct_type?.message
                                    ? errors?.fixed_amount_deduct_type?.message
                                    : ""
                                }
                                textBoxError={
                                  errors?.fixed_amount_deduct_type
                                    ? true
                                    : false
                                }
                              />
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={additionalInfoStyle}>
                          <Box sx={individualBoxStyle}>
                            <Typography variant="body2" color="text.secondary">
                              For every additional
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_additional_mins"
                              type="minutes"
                              value={watch("ot_fixed_amount_additional_mins")}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue(
                                  "ot_fixed_amount_additional_mins",
                                  value,
                                );
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Typography variant="body2" color="text.secondary">
                              mins an additional
                            </Typography>
                            <CustomBox
                              name="ot_fixed_amount_additional_payable"
                              type="amount"
                              value={watch(
                                "ot_fixed_amount_additional_payable",
                              )}
                              register={register}
                              errors={errors}
                              handleChange={(e, value) => {
                                setValue(
                                  "ot_fixed_amount_additional_payable",
                                  value,
                                );
                              }}
                              rules={{ required: "Enter value" }}
                              inputWidth="50px"
                            />

                            <Typography variant="body2" color="text.secondary">
                              will be payable{" "}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {watch("fixed_amount_deduct_type")}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={infoMessageStyle}>
                          <PaymentBasedFixedAmountExample />
                        </Box>
                      </>
                    )}
                  </>
                )}

                {/* <Box
                  sx={{
                    ml: 2,
                    mr: 2,
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ mb: 2 }}>
                      <Typography fontWeight="medium">
                        {" "}
                        Is OT pay added to payroll by default
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enable to auto-add approved OT to relevent
                        payroll.Disable to Keep OT in backlog for manual
                        addition later.
                      </Typography>
                    </Box>
                    <Switch
                      checked={watch("is_ot_pay_by_default")}
                      onChange={handleOTPay}
                    />
                  </Box>
                </Box> */}
              </>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle}>
              <Typography fontWeight="medium">Weekoff</Typography>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">Is Weekoff Paid</Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  checked={watch("is_weekoff_paid")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_weekoff_paid", checked);
                  }}
                />
              </Box>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">
                  As per weekday calculations
                </Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  checked={watch("as_per_week_day_for_week_off")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("as_per_week_day_for_week_off", checked);
                    if (!checked) {
                      setValue("payment_for_week_off_attendance", false);
                    }
                    turnOffWeekoffTimeBoundBreak();
                  }}
                />
              </Box>
            </Box>
            {!watch("as_per_week_day_for_week_off") && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ ml: 2, mb: 2 }}
              >
                <Box>
                  <Typography fontWeight="medium">
                    Payment for weekoff day attendance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pay employees who work on a weekoff.
                  </Typography>
                </Box>
                <Box sx={{ mr: 1.6 }}>
                  <Switch
                    checked={watch("payment_for_week_off_attendance")}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setValue(
                        "payment_for_week_off_attendance",
                        event.target.checked,
                      );
                      if (!checked) {
                        resetWeekOffFields();
                      } else {
                        setValue(
                          "week_off_work_pre_authorization",
                          watch("week_off_work_pre_authorization") ||
                            "Automatic",
                        );
                        setValue(
                          "weekoff_calculation_based_on",
                          watch("weekoff_calculation_based_on") || "Hours",
                        );
                        setValue(
                          "is_weekoff_payment_based_on",
                          watch("is_weekoff_payment_based_on") || "Attendance",
                        );
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {watch("payment_for_week_off_attendance") &&
              !watch("as_per_week_day_for_week_off") && (
                <>
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Typography fontWeight="medium">
                      Weekoff work Preauthorization
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Define whether weekoff is auto-approved or requires manual
                      approval.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={watch("week_off_work_pre_authorization")}
                      onChange={handleWeekOffPreauthorization}
                      sx={optionsStyle}
                    >
                      {WeekoffPreauthorizationOptions.map((opt) => {
                        const isActive =
                          watch("week_off_work_pre_authorization") === opt.id;
                        return (
                          <Box key={opt.id} sx={cardStyle(isActive)}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Radio value={opt.id} />
                              <Typography
                                fontWeight="medium"
                                sx={{
                                  color: isActive
                                    ? themes.stepActiveColor
                                    : "inherit",
                                }}
                              >
                                {opt.label}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 4,
                                maxWidth: 250,
                                wordBreak: "break-word",
                              }}
                            >
                              {opt.description}
                            </Typography>
                          </Box>
                        );
                      })}
                    </RadioGroup>
                  </Box>
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Typography fontWeight="medium">
                      Calculation based on
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose whether weekoff is calculated based on hours worked
                      or entire shifts.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={watch("weekoff_calculation_based_on")}
                      onChange={hanldeWeekoffCalculationBasedOn}
                      sx={optionsStyle}
                    >
                      {weekoffCalculationOptions.map((opt) => {
                        const isActive =
                          watch("weekoff_calculation_based_on") === opt.id;
                        return (
                          <Box key={opt.id} sx={cardStyle(isActive)}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Radio value={opt.id} />
                              <Typography
                                fontWeight="medium"
                                sx={{
                                  color: isActive
                                    ? themes.stepActiveColor
                                    : "inherit",
                                }}
                              >
                                {opt.label}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 4,
                                maxWidth: 250,
                                wordBreak: "break-word",
                              }}
                            >
                              {opt.description}
                            </Typography>
                            {opt.id === "Shift" &&
                              watch("weekoff_calculation_based_on") ===
                                "Shift" && (
                                <Box sx={{ ...amtFactorStyle, mt: 0.5 }}>
                                  <Typography fontWeight="medium">
                                    Payment Factor{" "}
                                    <span style={{ color: themes?.redColor }}>
                                      *
                                    </span>
                                  </Typography>
                                  <CustomBox
                                    name="week_off_payment_factor"
                                    type="shift"
                                    value={watch("week_off_payment_factor")}
                                    register={register}
                                    errors={errors}
                                    handleChange={(e, value) => {
                                      setValue(
                                        "week_off_payment_factor",
                                        value,
                                      );
                                    }}
                                    rules={{ required: "Enter value" }}
                                    inputWidth="50px"
                                  />
                                </Box>
                              )}
                          </Box>
                        );
                      })}
                    </RadioGroup>
                  </Box>
                  {watch("weekoff_calculation_based_on") === "Shift" && (
                    <>
                      <Box sx={{ px: 2, py: 1, mb: 2 }}>
                        <ShiftWorkTable
                          rows={weekoffRows}
                          setRows={setWeekoffRows}
                          rowError={weekoffRowError}
                          setRowError={setWeekoffRowError}
                          onValidateRef={validateTableRefWeekoff}
                        />
                      </Box>
                      <Box sx={additionalInfoStyle}>
                        <Box sx={individualBoxStyle}>
                          <Typography variant="body2" color="text.secondary">
                            Each additional
                          </Typography>
                          <CustomBox
                            name="weekoff_partial_shift_additional_percentage"
                            type="percentage"
                            value={watch(
                              "weekoff_partial_shift_additional_percentage",
                            )}
                            register={register}
                            errors={errors}
                            handleChange={(e, value) => {
                              setValue(
                                "weekoff_partial_shift_additional_percentage",
                                value,
                              );
                            }}
                            rules={{ required: "Enter value" }}
                            inputWidth="50px"
                          />

                          <Typography variant="body2" color="text.secondary">
                            percentage is counted as
                          </Typography>
                          <CustomBox
                            name="weekoff_partial_shift_count"
                            type="shift"
                            value={watch("weekoff_partial_shift_count")}
                            register={register}
                            errors={errors}
                            handleChange={(e, value) => {
                              setValue("weekoff_partial_shift_count", value);
                            }}
                            rules={{ required: "Enter value" }}
                            inputWidth="50px"
                          />

                          <Typography variant="body2" color="text.secondary">
                            shift
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={infoMessageStyle}>
                        <ShiftWorkExample />
                      </Box>
                    </>
                  )}

                  {watch("weekoff_calculation_based_on") === "Hours" && (
                    <>
                      <Box sx={{ ml: 2, mb: 2 }}>
                        <Typography fontWeight="medium">
                          Payment Based On
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Decide whether payment is linked to attendance factor
                          or a fixed amount
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <RadioGroup
                          row
                          value={watch("is_weekoff_payment_based_on")}
                          onChange={handleWeekoffPaymentBasedOn}
                          sx={optionsStyle}
                        >
                          {weekoffPaymentBasedOptions.map((opt) => {
                            const isActive =
                              watch("is_weekoff_payment_based_on") === opt.id;
                            return (
                              <Box key={opt.id} sx={cardStyle(isActive)}>
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Radio value={opt.id} />
                                  <Typography
                                    fontWeight="medium"
                                    sx={{
                                      color: isActive
                                        ? themes.stepActiveColor
                                        : "inherit",
                                    }}
                                  >
                                    {opt.label}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    ml: 4,
                                    maxWidth: 250,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {opt.description}
                                </Typography>
                              </Box>
                            );
                          })}
                        </RadioGroup>
                      </Box>
                      {watch("is_weekoff_payment_based_on") === "Attendance" ? (
                        <>
                          {isBandAllowed && (
                            <Box sx={{ ...switchStyle, mb: 1 }}>
                              <Box>
                                <Typography fontWeight="medium">
                                  Is Weekoff Band Factor Applied
                                </Typography>
                              </Box>
                              <Box sx={{ mr: 1.6 }}>
                                <Switch
                                  checked={watch(
                                    "is_allow_payment_factor_for_weekoff",
                                  )}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setValue(
                                      "is_allow_payment_factor_for_weekoff",
                                      checked,
                                    );
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          {watch("is_allow_payment_factor_for_weekoff") &&
                            renderPaymentFactorSummary("weekoff")}
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                The threshold limit for weekoff is
                              </Typography>
                              <CustomBox
                                name="weekoff_thres_hold_limit"
                                type="minutes"
                                value={watch("weekoff_thres_hold_limit")}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue("weekoff_thres_hold_limit", value);
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins will be considered as
                              </Typography>
                              <CustomBox
                                name="weekoff_thres_hold_consideration"
                                type="minutes"
                                value={watch(
                                  "weekoff_thres_hold_consideration",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "weekoff_thres_hold_consideration",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins Per Day
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                After that, every additional
                              </Typography>
                              <CustomBox
                                name="weekoff_additional_thres_hold"
                                type="minutes"
                                value={watch("weekoff_additional_thres_hold")}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "weekoff_additional_thres_hold",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins will be considered as
                              </Typography>
                              <CustomBox
                                name="weekoff_additional_thres_hold_consideration"
                                type="minutes"
                                value={watch(
                                  "weekoff_additional_thres_hold_consideration",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "weekoff_additional_thres_hold_consideration",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins Weekoff Per Day
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={infoMessageStyle}>
                            <PaymentBasedWeekoffAttendanceExample />
                          </Box>
                          {watch("is_allow_payment_factor_for_weekoff") && (
                            <Box
                              sx={{
                                ...infoMessageStyle,
                                backgroundColor: "transparent",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <Checkbox
                                  checked={watch(
                                    "is_add_remaining_weekoff_to_base_factor",
                                  )}
                                  onChange={(event) =>
                                    setValue(
                                      "is_add_remaining_weekoff_to_base_factor",
                                      event.target.checked,
                                    )
                                  }
                                  sx={{ p: 0, mt: 0.3 }}
                                />
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography fontWeight="bold">
                                      Add remaining (unrounded) hours to 1.0
                                      Weekoff
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    When Weekoff hours are rounded (e.g., 1h
                                    45m → 1h 30m), the remaining 15 minutes
                                    will be added to payroll at 1.0 factor
                                    instead of being forfeited.
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (
                        <>
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                The threshold limit for weekoff is
                              </Typography>
                              <CustomBox
                                name="weekoff_fixed_amount_thres_hold"
                                type="minutes"
                                value={watch("weekoff_fixed_amount_thres_hold")}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "weekoff_fixed_amount_thres_hold",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins with an amount payable of
                              </Typography>
                              <CustomBox
                                name="weekoff_fixed_amount_thres_hold_consideration"
                                type="amount"
                                value={watch(
                                  "weekoff_fixed_amount_thres_hold_consideration",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "weekoff_fixed_amount_thres_hold_consideration",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                            </Box>
                          </Box>
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                For every additional
                              </Typography>
                              <CustomBox
                                name="weekoff_fixed_amount_additional_thres_hold"
                                type="minutes"
                                value={watch(
                                  "weekoff_fixed_amount_additional_thres_hold",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "weekoff_fixed_amount_additional_thres_hold",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins an additional
                              </Typography>
                              <CustomBox
                                name="weekoff_fixed_amount_additional_thres_hold_consideration"
                                type="amount"
                                value={watch(
                                  "weekoff_fixed_amount_additional_thres_hold_consideration",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "weekoff_fixed_amount_additional_thres_hold_consideration",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                will be payable{" "}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Per Day
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={infoMessageStyle}>
                            <PaymentBasedFixedAmountExample />
                          </Box>
                        </>
                      )}
                    </>
                  )}

                  {/* <Box
                    sx={{
                      ml: 2,
                      mr: 2,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ mb: 2 }}>
                        <Typography fontWeight="medium">
                          {" "}
                          Is weekoff pay added to payroll by default
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Enable to auto-add approved weekoff to relevent
                          payroll.Disable to Keep weekoff in backlog for manual
                          addition later.
                        </Typography>
                      </Box>
                      <Switch
                        checked={watch("is_weekoff_pay_by_default")}
                        onChange={handleWeekoffPay}
                      />
                    </Box>
                  </Box> */}
                </>
              )}
            {/* Hidden with the payment toggle when "As per weekday
                calculations" is on (the toggle can stay true underneath). */}
            {watch("payment_for_week_off_attendance") &&
              !watch("as_per_week_day_for_week_off") && (
              <>
                <Box sx={{ ...switchStyle, mb: 1 }}>
                  <Box>
                    <Typography fontWeight="medium" sx={{ mb: 0.5 }}>
                      Time-Bound Break Deductions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable this to deduct break time automatically based on
                      total hours worked.
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        mb: 1.5,
                        mt: 1,
                        px: 1.25,
                        py: 0.75,
                        backgroundColor: "#f0fdf4",
                        borderLeft: "3px solid #22c55e",
                        color: "#166534",
                        borderRadius: "0 4px 4px 0",
                        fontSize: 12,
                      }}
                    >
                      <strong>Note:</strong> If an employee records a break, it
                      counts toward the rule and only the remaining break time
                      is deducted.
                    </Box>
                  </Box>
                  <Box sx={{ mr: 1.6 }}>
                    <Switch
                      checked={watch(
                        "is_weekoff_time_bound_break_deduction_allowed",
                      )}
                      onChange={handleWeekoffTimeBoundBreakToggle}
                    />
                  </Box>
                </Box>
                {watch("is_weekoff_time_bound_break_deduction_allowed") && (
                  <>
                    <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={handleAddWeekoffTimeBoundBreakRule}
                          sx={{
                            minHeight: 38,
                            px: 2,
                            borderRadius: "10px",
                            border: "1px dashed #7BC9A7",
                            backgroundColor: "#F8FCFA",
                            color: "#1A5C42",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            cursor: "pointer",
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            width: "fit-content",
                            maxWidth: "100%",
                            "&:hover": {
                              backgroundColor: "#F1FAF5",
                              borderColor: "#4FB489",
                            },
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            sx={{
                              color: "#1A5C42",
                              fontSize: 14,
                              lineHeight: 1.25,
                            }}
                          >
                            + Add Time Boundary
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                      {weekoffTimeBoundBreakRules.map((rule, index) => (
                        <Box
                          key={`weekoff-time-bound-break-${index}`}
                          sx={{
                            backgroundColor: "#f4f6f9",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            p: 2,
                            mb: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2">
                                <strong>Rule {index + 1}:</strong> If total
                                worked hours exceed
                              </Typography>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  ampm={false}
                                  viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                  }}
                                  value={getTimeBoundBreakPickerValue(rule)}
                                  onChange={(newValue) =>
                                    handleWeekoffTimeBoundBreakTimeChange(
                                      index,
                                      newValue,
                                    )
                                  }
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      error: Boolean(
                                        weekoffTimeBoundBreakErrors?.[index]
                                          ?.hours ||
                                        weekoffTimeBoundBreakErrors?.[index]
                                          ?.minutes,
                                      ),
                                      sx: { width: 130 },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>
                            <Box
                              onClick={() =>
                                weekoffTimeBoundBreakRules.length > 1 &&
                                handleRemoveWeekoffTimeBoundBreakRule(index)
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                cursor:
                                  weekoffTimeBoundBreakRules.length > 1
                                    ? "pointer"
                                    : "not-allowed",
                                color:
                                  weekoffTimeBoundBreakRules.length > 1
                                    ? "#dc2626"
                                    : "#9ca3af",
                                fontWeight: 600,
                                fontSize: 13,
                                "&:hover": {
                                  textDecoration:
                                    weekoffTimeBoundBreakRules.length > 1
                                      ? "underline"
                                      : "none",
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                              Remove
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              ml: 5.5,
                              color: "#4b5563",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "inherit" }}
                            >
                              Apply:
                            </Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={rule.deduct_minutes}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (
                                  /^\d{0,3}$/.test(val) &&
                                  (val === "" || Number(val) <= 120)
                                ) {
                                  handleWeekoffTimeBoundBreakRuleChange(
                                    index,
                                    "deduct_minutes",
                                    val,
                                  );
                                }
                              }}
                              error={Boolean(
                                weekoffTimeBoundBreakErrors?.[index]
                                  ?.deduct_minutes,
                              )}
                              inputProps={{
                                min: 1,
                                max: 120,
                                style: {
                                  textAlign: "center",
                                  width: "50px",
                                  padding: "4px 6px",
                                },
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "inherit" }}
                            >
                              mins break at
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 115 }}>
                              <Select
                                value={rule.deduct_from || "start_time"}
                                onChange={(event) =>
                                  handleWeekoffTimeBoundBreakRuleChange(
                                    index,
                                    "deduct_from",
                                    event.target.value,
                                  )
                                }
                                sx={{
                                  backgroundColor: "#fff",
                                  "& .MuiSelect-select": {
                                    py: "6px",
                                  },
                                }}
                              >
                                {timeBoundBreakDeductFromOptions.map(
                                  (option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </MenuItem>
                                  ),
                                )}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                      ))}
                      {weekoffTimeBoundBreakErrors.some(
                        (row) =>
                          row?.hours || row?.minutes || row?.deduct_minutes,
                      ) && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mb: 1 }}
                        >
                          Enter a valid threshold (0-24 hrs, 0-59 mins) and a
                          deduction between 1-120 mins for each rule.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle}>
              <Typography fontWeight="medium">Holiday</Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">Is Holiday Paid</Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  checked={watch("is_holiday_paid")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("is_holiday_paid", checked);
                  }}
                />
              </Box>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ ml: 2, mb: 2 }}
            >
              <Box>
                <Typography fontWeight="medium">
                  As per weekday calculations
                </Typography>
              </Box>
              <Box sx={{ mr: 1.6 }}>
                <Switch
                  checked={watch("as_per_week_day_for_holiday")}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setValue("as_per_week_day_for_holiday", checked);
                    if (!checked) {
                      setValue("is_payment_for_holiday_day_attendance", false);
                    }
                    turnOffHolidayTimeBoundBreak();
                  }}
                />
              </Box>
            </Box>

            {!watch("as_per_week_day_for_holiday") && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ ml: 2, mb: 2 }}
              >
                <Box>
                  <Typography fontWeight="medium">
                    Payment for holiday day attendance
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pay employees who work on a holidays.
                  </Typography>
                </Box>
                <Box sx={{ mr: 1.6 }}>
                  <Switch
                    {...register("is_payment_for_holiday_day_attendance")}
                    checked={watch("is_payment_for_holiday_day_attendance")}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setValue(
                        "is_payment_for_holiday_day_attendance",
                        checked,
                      );
                      if (!checked) {
                        resetHolidayPaymentFields();
                      } else {
                        setValue(
                          "holiday_work_preauthorization",
                          watch("holiday_work_preauthorization") || "Automatic",
                        );
                        setValue(
                          "holiday_calculation_based_on",
                          watch("holiday_calculation_based_on") || "Hours",
                        );
                        setValue(
                          "is_holiday_payment_based_on",
                          watch("is_holiday_payment_based_on") || "Attendance",
                        );
                      }
                    }}
                  />
                </Box>
              </Box>
            )}
            {watch("is_payment_for_holiday_day_attendance") &&
              !watch("as_per_week_day_for_holiday") && (
                <>
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Typography fontWeight="medium">
                      Holiday work Preauthorization
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Define whether holiday is auto-approved or requires manual
                      approval.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={watch("holiday_work_preauthorization")}
                      onChange={handleHolidayPreauthorization}
                      sx={optionsStyle}
                    >
                      {holidayPreauthorizationOptions.map((opt) => {
                        const isActive =
                          watch("holiday_work_preauthorization") === opt.id;
                        return (
                          <Box key={opt.id} sx={cardStyle(isActive)}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Radio value={opt.id} />
                              <Typography
                                fontWeight="medium"
                                sx={{
                                  color: isActive
                                    ? themes.stepActiveColor
                                    : "inherit",
                                }}
                              >
                                {opt.label}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 4,
                                maxWidth: 250,
                                wordBreak: "break-word",
                              }}
                            >
                              {opt.description}
                            </Typography>
                          </Box>
                        );
                      })}
                    </RadioGroup>
                  </Box>
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Typography fontWeight="medium">
                      Calculation based on
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose whether holiday is calculated based on hours worked
                      or entire shifts.
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      value={watch("holiday_calculation_based_on")}
                      onChange={hanldeHolidayCalculationBasedOn}
                      sx={optionsStyle}
                    >
                      {holidayCalculationOptions.map((opt) => {
                        const isActive =
                          watch("holiday_calculation_based_on") === opt.id;
                        return (
                          <Box key={opt.id} sx={cardStyle(isActive)}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Radio value={opt.id} />
                              <Typography
                                fontWeight="medium"
                                sx={{
                                  color: isActive
                                    ? themes.stepActiveColor
                                    : "inherit",
                                }}
                              >
                                {opt.label}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                ml: 4,
                                maxWidth: 250,
                                wordBreak: "break-word",
                              }}
                            >
                              {opt.description}
                            </Typography>
                            {opt.id === "Shift" &&
                              watch("holiday_calculation_based_on") ===
                                "Shift" && (
                                <Box sx={{ ...amtFactorStyle, mt: 0.5 }}>
                                  <Typography fontWeight="medium">
                                    Payment Factor{" "}
                                    <span style={{ color: themes?.redColor }}>
                                      *
                                    </span>
                                  </Typography>
                                  <CustomBox
                                    name="holiday_sft_payment_factor"
                                    type="shift"
                                    value={watch("holiday_sft_payment_factor")}
                                    register={register}
                                    errors={errors}
                                    handleChange={(e, value) => {
                                      setValue(
                                        "holiday_sft_payment_factor",
                                        value,
                                      );
                                    }}
                                    rules={{ required: "Enter value" }}
                                    inputWidth="50px"
                                  />
                                </Box>
                              )}
                          </Box>
                        );
                      })}
                    </RadioGroup>
                  </Box>
                  {watch("holiday_calculation_based_on") === "Shift" && (
                    <>
                      <Box sx={{ px: 2, py: 1, mb: 2 }}>
                        <ShiftWorkTable
                          rows={holidayRows}
                          setRows={setHolidayRows}
                          rowError={holidayRowError}
                          setRowError={setHolidayRowError}
                          onValidateRef={validateTableRefHoliday}
                        />
                      </Box>
                      <Box sx={additionalInfoStyle}>
                        <Box sx={individualBoxStyle}>
                          <Typography variant="body2" color="text.secondary">
                            Each additional
                          </Typography>
                          <CustomBox
                            name="holiday_partial_shift_additional_percentage"
                            type="percentage"
                            value={watch(
                              "holiday_partial_shift_additional_percentage",
                            )}
                            register={register}
                            errors={errors}
                            handleChange={(e, value) => {
                              setValue(
                                "holiday_partial_shift_additional_percentage",
                                value,
                              );
                            }}
                            rules={{ required: "Enter value" }}
                            inputWidth="50px"
                          />
                          <Typography variant="body2" color="text.secondary">
                            percentage is counted as
                          </Typography>
                          <CustomBox
                            name="holiday_partial_shift_count"
                            type="shift"
                            value={watch("holiday_partial_shift_count")}
                            register={register}
                            errors={errors}
                            handleChange={(e, value) => {
                              setValue("holiday_partial_shift_count", value);
                            }}
                            rules={{ required: "Enter value" }}
                            inputWidth="50px"
                          />
                          <Typography variant="body2" color="text.secondary">
                            shift
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={infoMessageStyle}>
                        <ShiftWorkExample />
                      </Box>
                    </>
                  )}

                  {watch("holiday_calculation_based_on") === "Hours" && (
                    <>
                      <Box sx={{ ml: 2, mb: 2 }}>
                        <Typography fontWeight="medium">
                          Payment Based On
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Decide whether payment is linked to attendance factor
                          or a fixed amount
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <RadioGroup
                          row
                          value={watch("is_holiday_payment_based_on")}
                          onChange={handleHolidayPaymentBasedOn}
                          sx={optionsStyle}
                        >
                          {holidayPaymentBasedOptions.map((opt) => {
                            const isActive =
                              watch("is_holiday_payment_based_on") === opt.id;
                            return (
                              <Box key={opt.id} sx={cardStyle(isActive)}>
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Radio value={opt.id} />
                                  <Typography
                                    fontWeight="medium"
                                    sx={{
                                      color: isActive
                                        ? themes.stepActiveColor
                                        : "inherit",
                                    }}
                                  >
                                    {opt.label}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    ml: 4,
                                    maxWidth: 250,
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {opt.description}
                                </Typography>
                              </Box>
                            );
                          })}
                        </RadioGroup>
                      </Box>
                      {watch("is_holiday_payment_based_on") === "Attendance" ? (
                        <>
                          {isBandAllowed && (
                            <Box sx={{ ...switchStyle, mb: 1 }}>
                              <Box>
                                <Typography fontWeight="medium">
                                  Is Holiday Band Factor Applied
                                </Typography>
                              </Box>
                              <Box sx={{ mr: 1.6 }}>
                                <Switch
                                  checked={watch(
                                    "is_allow_payment_factor_for_holiday",
                                  )}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setValue(
                                      "is_allow_payment_factor_for_holiday",
                                      checked,
                                    );
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          {watch("is_allow_payment_factor_for_holiday") &&
                            renderPaymentFactorSummary("holiday")}
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                The threshold limit for holiday is
                              </Typography>
                              <CustomBox
                                name="holiday_thres_hold_limit"
                                type="minutes"
                                value={watch("holiday_thres_hold_limit")}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue("holiday_thres_hold_limit", value);
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins will be considered as
                              </Typography>
                              <CustomBox
                                name="holiday_thres_hold_consideration"
                                type="minutes"
                                value={watch(
                                  "holiday_thres_hold_consideration",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "holiday_thres_hold_consideration",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins Per Day
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                After that, every additional
                              </Typography>
                              <CustomBox
                                name="holiday_additional_thres_hold"
                                type="minutes"
                                value={watch("holiday_additional_thres_hold")}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "holiday_additional_thres_hold",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins will be considered as
                              </Typography>
                              <CustomBox
                                name="holiday_additional_thres_hold_consideration"
                                type="minutes"
                                value={watch(
                                  "holiday_additional_thres_hold_consideration",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "holiday_additional_thres_hold_consideration",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins Holiday Per Day
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={infoMessageStyle}>
                            <PaymentBasedHolidayAttendanceExample />
                          </Box>
                          {watch("is_allow_payment_factor_for_holiday") && (
                            <Box
                              sx={{
                                ...infoMessageStyle,
                                backgroundColor: "transparent",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <Checkbox
                                  checked={watch(
                                    "is_add_remaining_holiday_to_base_factor",
                                  )}
                                  onChange={(event) =>
                                    setValue(
                                      "is_add_remaining_holiday_to_base_factor",
                                      event.target.checked,
                                    )
                                  }
                                  sx={{ p: 0, mt: 0.3 }}
                                />
                                <Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography fontWeight="bold">
                                      Add remaining (unrounded) hours to 1.0
                                      Holiday
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                  >
                                    When Holiday hours are rounded (e.g., 1h
                                    45m → 1h 30m), the remaining 15 minutes
                                    will be added to payroll at 1.0 factor
                                    instead of being forfeited.
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (
                        <>
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                The threshold limit for holiday is
                              </Typography>
                              <CustomBox
                                name="holiday_fixed_amount_thres_hold"
                                type="minutes"
                                value={watch("holiday_fixed_amount_thres_hold")}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "holiday_fixed_amount_thres_hold",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins with an amount payable of
                              </Typography>
                              <CustomBox
                                name="holiday_fixed_amount_payable"
                                type="amount"
                                value={watch("holiday_fixed_amount_payable")}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "holiday_fixed_amount_payable",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Per Day
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={additionalInfoStyle}>
                            <Box sx={individualBoxStyle}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                For every additional
                              </Typography>
                              <CustomBox
                                name="holiday_fixed_amount_additional_mins"
                                type="minutes"
                                value={watch(
                                  "holiday_fixed_amount_additional_mins",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "holiday_fixed_amount_additional_mins",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                mins an additional
                              </Typography>
                              <CustomBox
                                name="holiday_fixed_amount_additional_payable"
                                type="amount"
                                value={watch(
                                  "holiday_fixed_amount_additional_payable",
                                )}
                                register={register}
                                errors={errors}
                                handleChange={(e, value) => {
                                  setValue(
                                    "holiday_fixed_amount_additional_payable",
                                    value,
                                  );
                                }}
                                rules={{ required: "Enter value" }}
                                inputWidth="50px"
                              />

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                will be payable{" "}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Per Day
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={infoMessageStyle}>
                            <PaymentBasedFixedAmountExample />
                          </Box>
                        </>
                      )}
                    </>
                  )}

                  {/* <Box
                    sx={{
                      ml: 2,
                      mr: 2,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ mb: 2 }}>
                        <Typography fontWeight="medium">
                          {" "}
                          Is Holiday pay added to payroll by default
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Enable to auto-add approved holiday to relevent
                          payroll.Disable to Keep holiday in backlog for manual
                          addition later.
                        </Typography>
                      </Box>
                      <Switch
                        checked={watch("holiday_added_payroll_default")}
                        onChange={handleHolidayPay}
                      />
                    </Box>
                  </Box> */}
                </>
              )}

            {/* Hidden with the payment toggle when "As per weekday
                calculations" is on (the toggle can stay true underneath). */}
            {watch("is_payment_for_holiday_day_attendance") &&
              !watch("as_per_week_day_for_holiday") && (
              <>
                <Box sx={{ ...switchStyle, mb: 1 }}>
                  <Box>
                    <Typography fontWeight="medium" sx={{ mb: 0.5 }}>
                      Time-Bound Break Deductions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable this to deduct break time automatically based on
                      total hours worked.
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-block",
                        mb: 1.5,
                        mt: 1,
                        px: 1.25,
                        py: 0.75,
                        backgroundColor: "#f0fdf4",
                        borderLeft: "3px solid #22c55e",
                        color: "#166534",
                        borderRadius: "0 4px 4px 0",
                        fontSize: 12,
                      }}
                    >
                      <strong>Note:</strong> If an employee records a break, it
                      counts toward the rule and only the remaining break time
                      is deducted.
                    </Box>
                  </Box>
                  <Box sx={{ mr: 1.6 }}>
                    <Switch
                      checked={watch(
                        "is_holiday_time_bound_break_deduction_allowed",
                      )}
                      onChange={handleHolidayTimeBoundBreakToggle}
                    />
                  </Box>
                </Box>
                {watch("is_holiday_time_bound_break_deduction_allowed") && (
                  <>
                    <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          component="button"
                          type="button"
                          onClick={handleAddHolidayTimeBoundBreakRule}
                          sx={{
                            minHeight: 38,
                            px: 2,
                            borderRadius: "10px",
                            border: "1px dashed #7BC9A7",
                            backgroundColor: "#F8FCFA",
                            color: "#1A5C42",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            cursor: "pointer",
                            textAlign: "left",
                            whiteSpace: "nowrap",
                            width: "fit-content",
                            maxWidth: "100%",
                            "&:hover": {
                              backgroundColor: "#F1FAF5",
                              borderColor: "#4FB489",
                            },
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            sx={{
                              color: "#1A5C42",
                              fontSize: 14,
                              lineHeight: 1.25,
                            }}
                          >
                            + Add Time Boundary
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ ml: 2, mr: 2, mb: 2 }}>
                      {holidayTimeBoundBreakRules.map((rule, index) => (
                        <Box
                          key={`holiday-time-bound-break-${index}`}
                          sx={{
                            backgroundColor: "#f4f6f9",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            p: 2,
                            mb: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2">
                                <strong>Rule {index + 1}:</strong> If total
                                worked hours exceed
                              </Typography>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  ampm={false}
                                  viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                  }}
                                  value={getTimeBoundBreakPickerValue(rule)}
                                  onChange={(newValue) =>
                                    handleHolidayTimeBoundBreakTimeChange(
                                      index,
                                      newValue,
                                    )
                                  }
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      error: Boolean(
                                        holidayTimeBoundBreakErrors?.[index]
                                          ?.hours ||
                                        holidayTimeBoundBreakErrors?.[index]
                                          ?.minutes,
                                      ),
                                      sx: { width: 130 },
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>
                            <Box
                              onClick={() =>
                                holidayTimeBoundBreakRules.length > 1 &&
                                handleRemoveHolidayTimeBoundBreakRule(index)
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                cursor:
                                  holidayTimeBoundBreakRules.length > 1
                                    ? "pointer"
                                    : "not-allowed",
                                color:
                                  holidayTimeBoundBreakRules.length > 1
                                    ? "#dc2626"
                                    : "#9ca3af",
                                fontWeight: 600,
                                fontSize: 13,
                                "&:hover": {
                                  textDecoration:
                                    holidayTimeBoundBreakRules.length > 1
                                      ? "underline"
                                      : "none",
                                },
                              }}
                            >
                              <Delete fontSize="small" />
                              Remove
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              ml: 5.5,
                              color: "#4b5563",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "inherit" }}
                            >
                              Apply:
                            </Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={rule.deduct_minutes}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (
                                  /^\d{0,3}$/.test(val) &&
                                  (val === "" || Number(val) <= 120)
                                ) {
                                  handleHolidayTimeBoundBreakRuleChange(
                                    index,
                                    "deduct_minutes",
                                    val,
                                  );
                                }
                              }}
                              error={Boolean(
                                holidayTimeBoundBreakErrors?.[index]
                                  ?.deduct_minutes,
                              )}
                              inputProps={{
                                min: 1,
                                max: 120,
                                style: {
                                  textAlign: "center",
                                  width: "50px",
                                  padding: "4px 6px",
                                },
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: "inherit" }}
                            >
                              mins break at
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 115 }}>
                              <Select
                                value={rule.deduct_from || "start_time"}
                                onChange={(event) =>
                                  handleHolidayTimeBoundBreakRuleChange(
                                    index,
                                    "deduct_from",
                                    event.target.value,
                                  )
                                }
                                sx={{
                                  backgroundColor: "#fff",
                                  "& .MuiSelect-select": {
                                    py: "6px",
                                  },
                                }}
                              >
                                {timeBoundBreakDeductFromOptions.map(
                                  (option) => (
                                    <MenuItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </MenuItem>
                                  ),
                                )}
                              </Select>
                            </FormControl>
                          </Box>
                        </Box>
                      ))}
                      {holidayTimeBoundBreakErrors.some(
                        (row) =>
                          row?.hours || row?.minutes || row?.deduct_minutes,
                      ) && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mb: 1 }}
                        >
                          Enter a valid threshold (0-24 hrs, 0-59 mins) and a
                          deduction between 1-120 mins for each rule.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>

          <Box sx={mainOptions}>
            <Box sx={boxStyle(watch("is_absence_paid"))}>
              <Typography fontWeight="medium">Is Absence Paid</Typography>
              <Switch
                checked={watch("is_absence_paid")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_absence_paid", event.target.checked);
                  if (!checked) {
                    setValue("absent_payment_based_on", "Paid");
                    resetField("partial_payment_factor");
                    setValue("partial_payment_factor", 0);
                  }
                }}
              />
            </Box>
            {watch("is_absence_paid") && (
              <>
                <Box sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    value={watch("absent_payment_based_on")}
                    onChange={handleAbsencePaymentBasedOn}
                    sx={optionsStyle}
                  >
                    {isAbsenceOptions.map((opt) => {
                      const isActive =
                        watch("absent_payment_based_on") === opt.id;
                      return (
                        <Box key={opt.id} sx={cardStyle(isActive)}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Radio value={opt.id} />
                            <Typography
                              fontWeight="medium"
                              sx={{
                                color: isActive
                                  ? themes.stepActiveColor
                                  : "inherit",
                              }}
                            >
                              {opt.label}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              ml: 4,
                              maxWidth: 250,
                              wordBreak: "break-word",
                            }}
                          >
                            {opt.description}
                          </Typography>
                          {opt.id === "Partially Paid" &&
                            watch("absent_payment_based_on") ===
                              "Partially Paid" && (
                              <Box sx={amtFactorStyle}>
                                <Typography fontWeight="medium">
                                  Payment Factor{" "}
                                  <span style={{ color: themes?.redColor }}>
                                    *
                                  </span>
                                </Typography>
                                <CustomBox
                                  name="partial_payment_factor"
                                  type="shift"
                                  value={watch("partial_payment_factor")}
                                  register={register}
                                  errors={errors}
                                  handleChange={(e, value) => {
                                    setValue("partial_payment_factor", value);
                                  }}
                                  rules={{ required: "Enter value" }}
                                  inputWidth="50px"
                                />
                              </Box>
                            )}
                        </Box>
                      );
                    })}
                  </RadioGroup>
                </Box>
                {watch("absent_payment_based_on") === "Paid" ? (
                  <Box sx={infoMessageStyle}>
                    <IsAbsencePaidExample />
                  </Box>
                ) : (
                  <Box sx={infoMessageStyle}>
                    <IsAbsenceExample />
                  </Box>
                )}
              </>
            )}
          </Box>

          {renderNonOtPaymentFactorDialog()}

          <Box
            sx={{
              ml: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <CustomButton
                actionName="Prev"
                typeName="button"
                actionFuntion={() => {
                  handleBack(0);
                }}
              />
            </Box>
            <Box>
              <CustomButton actionName="Next" typeName="submit" />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
